import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Package, Boxes, ChevronLeft, ChevronDown, Mail, MessageSquare, Plus, Trash2, Pencil, ExternalLink, MessageCircle, Eye, EyeOff, Truck, Send, CheckCircle2, XCircle, RefreshCw, BarChart3, Megaphone, Image as ImageIcon, Clock, Upload, BookOpen, Lock, Star, Globe, Paperclip, Reply, X, Copy, ClipboardList, PackageCheck, Download, LayoutDashboard, TrendingUp, Sparkles, Bot, Printer, Landmark, CreditCard, Users, HeartHandshake, Gift, Search, Loader2 } from 'lucide-react';
import { printPackingSlip } from './packingSlip';
import Logo from '../../components/Logo';
import { CATEGORIES } from '../../mock/mock';
import {
  adminGetOrders, adminUpdateOrder, adminEditOrderItems, adminUploadShippingLabel, adminBulkShippingLabels, adminGetProducts, adminCreateProduct,
  adminUpdateProduct, adminDeleteProduct, adminSetProductStock, adminToggleVisibility, adminGetContacts, adminGetEmailLogs, adminReplyContact,
  adminGetDiscounts, adminCreateDiscount, adminUpdateDiscount, adminDeleteDiscount, adminGetDiscountUsage, adminSoldoutToPreorder,
  adminGetSettings, adminUpdateSettings, adminSendDigestNow, adminAiGenerate, adminGetEmailStatus, adminSendTestEmail, adminGetProfitLoss,
  adminGetPromotions, adminCreatePromotion, adminUpdatePromotion, adminDeletePromotion,
  adminGetIncoming, adminCreateIncoming, adminUpdateIncoming, adminDeleteIncoming, adminUploadImage,
  adminUploadProductCoa, adminDeleteProductCoa,
  adminSetLibraryPassword, adminGetLibraryStatus, adminGetLibraryItems, adminCreateLibraryItem, adminUpdateLibraryItem, adminDeleteLibraryItem, adminSendLibraryPassword, adminSendReviewRequest, adminSetPaymentMethod, adminResendConfirmation, adminEditOrderCustomer,
  adminGetLibraryAccess, adminAddLibraryAccess, adminSetLibraryAccessStatus, adminDeleteLibraryAccess,
  adminGetReviews, adminCreateReview, adminUpdateReview, adminDeleteReview, adminSetItemPreorder,
  adminSetOrderDiscountCode, adminPayCommissionCredit,
  http, authCfg,
} from '../../lib/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Box, Stat } from './AdminUI';
import StockPanel from './StockPanel';
import CampaignPanel from './CampaignPanel';
import ConciergeLogPanel from './ConciergeLogPanel';
import FulfillmentPanel from './FulfillmentPanel';
import ForecastPanel from './ForecastPanel';
import FinancialDashboard from './FinancialDashboard';
import MonthlyMoney from './MonthlyMoney';
import CrmPanel from './CrmPanel';
import CrewAdminPanel from './CrewAdminPanel';
import AbandonedPanel from './AbandonedPanel';
import WinBackPanel from './WinBackPanel';
import BlackFridayPanel from './BlackFridayPanel';
import BirthdayPanel from './BirthdayPanel';
import GiftCardsPanel from './GiftCardsPanel';
import PayrollPanel from './PayrollPanel';
import AdminAssistant from './AdminAssistant';
import TabInsights from './TabInsights';
import OverviewPanel from './OverviewPanel';
import {
  ORDER_STATUSES, STOCK_STATUSES, CARRIERS, trackingUrl, whatsappLink, smsLink,
  reviewWhatsappLink, isReviewDue, daysSincePlaced,
  EMPTY_PRODUCT, EMPTY_DISCOUNT, PROMO_TYPES, EMPTY_PROMO, EMPTY_INCOMING, EMPTY_LIB_ITEM, EMPTY_REVIEW,
} from './adminConstants';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { motion } from 'framer-motion';

const CAN_SWIPE = typeof window !== 'undefined' && (window.matchMedia('(pointer: coarse)').matches || window.innerWidth < 1024);

function SwipeRow({ action, onAction, children }) {
  const [swiping, setSwiping] = useState(false);
  if (!action) return children;
  return (
    <div className="relative overflow-hidden" data-testid={action.testid}>
      {swiping && (
        <div className={`absolute inset-0 flex items-center pl-5 ${action.bg}`}>
          <span className={`inline-flex items-center gap-2 text-xs font-semibold tracking-[0.15em] uppercase ${action.fg}`}>{action.label} &rarr;</span>
        </div>
      )}
      <motion.div drag="x" dragConstraints={{ left: 0, right: 110 }} dragElastic={0.15} dragSnapToOrigin dragDirectionLock
        onDragStart={() => setSwiping(true)}
        onDragEnd={(e, info) => { setSwiping(false); if (info.offset.x > 80) onAction(); }}>
        {children}
      </motion.div>
    </div>
  );
}


// Australia Post CSV enum normalizers (keep dropdowns showing valid values).
const _yn = (v) => (String(v || '').trim().toLowerCase() === 'yes' || v === true ? 'YES' : 'NO');
const _svc = (v) => (['pp', 'std', 'parcel post'].includes(String(v || 'EXP').trim().toLowerCase()) ? 'PP' : 'EXP');


const TABS = {
  overview: { label: 'Overview', icon: LayoutDashboard, testid: 'tab-overview' },
  orders: { label: 'Orders', icon: Package, testid: 'tab-orders' },
  fulfillment: { label: 'Fulfillment', icon: PackageCheck, testid: 'tab-fulfillment' },
  abandoned: { label: 'Abandoned', icon: Clock, testid: 'tab-abandoned' },
  giftcards: { label: 'Gift Cards', icon: Gift, testid: 'tab-giftcards' },
  winback: { label: 'Win-Back', icon: HeartHandshake, testid: 'tab-winback' },
  birthdays: { label: 'Birthdays', icon: Gift, testid: 'tab-birthdays' },
  crm: { label: 'CRM', icon: Users, testid: 'tab-crm' },
  crew: { label: 'Crew', icon: ClipboardList, testid: 'tab-crew' },
  findash: { label: 'Dashboard', icon: Sparkles, testid: 'tab-findash' },
  money: { label: 'Monthly Money', icon: BarChart3, testid: 'tab-money' },
  pnl: { label: 'Profit / Loss', icon: BarChart3, testid: 'tab-pnl' },
  payroll: { label: 'Payroll & Invoices', icon: Landmark, testid: 'tab-payroll' },
  forecast: { label: 'Forecast', icon: TrendingUp, testid: 'tab-forecast' },
  assistant: { label: 'AI Advisor', icon: Bot, testid: 'tab-assistant' },
  discounts: { label: 'Discount Codes', icon: Plus, testid: 'tab-discounts' },
  products: { label: 'Products', icon: Boxes, testid: 'tab-products' },
  stock: { label: 'Stock Take', icon: ClipboardList, testid: 'tab-stock' },
  marketing: { label: 'Marketing', icon: Megaphone, testid: 'tab-marketing' },
  blackfriday: { label: 'Black Friday', icon: Sparkles, testid: 'tab-blackfriday' },
  reviews: { label: 'Reviews', icon: Star, testid: 'tab-reviews' },
  seo: { label: 'SEO', icon: Globe, testid: 'tab-seo' },
  library: { label: 'Client Library', icon: BookOpen, testid: 'tab-library' },
  concierge: { label: 'Concierge Log', icon: Sparkles, testid: 'tab-concierge' },
  contacts: { label: 'Contacts', icon: MessageSquare, testid: 'tab-contacts' },
  emails: { label: 'Emails', icon: Mail, testid: 'tab-emails' },
  settings: { label: 'Shipping & Bank', icon: Truck, testid: 'tab-settings' },
};

const TAB_GROUPS = [
  { key: 'home', label: 'Overview', icon: LayoutDashboard, tabs: ['overview'] },
  { key: 'sales', label: 'Orders', icon: Package, tabs: ['orders', 'fulfillment', 'abandoned'] },
  { key: 'customers', label: 'Customers', icon: Users, tabs: ['crm', 'winback', 'birthdays', 'giftcards', 'reviews', 'contacts'] },
  { key: 'marketing', label: 'Marketing', icon: Megaphone, tabs: ['discounts', 'blackfriday', 'marketing', 'seo', 'emails'] },
  { key: 'financial', label: 'Money', icon: BarChart3, tabs: ['findash', 'money', 'pnl', 'payroll', 'forecast'] },
  { key: 'inventory', label: 'Store', icon: Boxes, tabs: ['products', 'stock', 'library', 'settings'] },
  { key: 'crew', label: 'Crew', icon: ClipboardList, tabs: ['crew'] },
  { key: 'advisor', label: 'AI', icon: Bot, tabs: ['assistant', 'concierge'] },
];


export default function AdminDashboard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [replyingContact, setReplyingContact] = useState(null);
  const [replySubject, setReplySubject] = useState('Re: Your enquiry \u2014 Australian Luxe Peptides');
  const [replyMessage, setReplyMessage] = useState('');
  const [replyFiles, setReplyFiles] = useState([]);
  const [replyCoaId, setReplyCoaId] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [emails, setEmails] = useState([]);
  const [emailSearch, setEmailSearch] = useState('');
  const [emailPreview, setEmailPreview] = useState(null);
  const [discounts, setDiscounts] = useState([]);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [discountUsage, setDiscountUsage] = useState([]);
  const [expandedCode, setExpandedCode] = useState('');
  const [editing, setEditing] = useState(null);
  const [postage, setPostage] = useState({});
  const [notesDraft, setNotesDraft] = useState({});
  const [notifyMap, setNotifyMap] = useState({});
  const [itemEditOrder, setItemEditOrder] = useState(null);
  const [itemEditLines, setItemEditLines] = useState([]);
  const [itemEditNotify, setItemEditNotify] = useState(false);
  const [itemEditAddId, setItemEditAddId] = useState('');
  const [itemEditSaving, setItemEditSaving] = useState(false);
  const [expandedOrders, setExpandedOrders] = useState({});
  const [custEditOrder, setCustEditOrder] = useState(null);
  const [custDraft, setCustDraft] = useState({});
  const [custSaving, setCustSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [activeSection, setActiveSection] = useState('home');
  const [orderSearch, setOrderSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [ordersShown, setOrdersShown] = useState(20);
  const [storeSettings, setStoreSettings] = useState(null);
  const [settingsDraft, setSettingsDraft] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);
  const [emailStatusLoading, setEmailStatusLoading] = useState(false);
  const [testEmailTo, setTestEmailTo] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [pnl, setPnl] = useState(null);
  const [discountSearch, setDiscountSearch] = useState('');
  const [discountFilter, setDiscountFilter] = useState('all');
  const [discountsLoaded, setDiscountsLoaded] = useState(false);
  const [pnlLoading, setPnlLoading] = useState(false);
  const [pnlStart, setPnlStart] = useState('');
  const [pnlEnd, setPnlEnd] = useState('');
  const [promotions, setPromotions] = useState([]);
  const [editingPromo, setEditingPromo] = useState(null);
  const [incoming, setIncoming] = useState([]);
  const [editingIncoming, setEditingIncoming] = useState(null);
  const [heroUploading, setHeroUploading] = useState(false);
  const [pnlOrdersFor, setPnlOrdersFor] = useState(null);
  const [libItems, setLibItems] = useState([]);
  const [libStatus, setLibStatus] = useState(null);
  const [editingLibItem, setEditingLibItem] = useState(null);
  const [newLibPassword, setNewLibPassword] = useState('');
  const [libImgUploading, setLibImgUploading] = useState(false);
  const [prodImgUploading, setProdImgUploading] = useState(false);
  const [coaUploading, setCoaUploading] = useState(false);
  const [libAccess, setLibAccess] = useState([]);
  const [newAccessEmail, setNewAccessEmail] = useState('');
  const [reviews, setReviews] = useState([]);
  const [editingReview, setEditingReview] = useState(null);

  const getPostage = (o) => ({
    carrier: postage[o.orderNumber]?.carrier ?? o.carrier ?? '',
    tracking: postage[o.orderNumber]?.tracking ?? o.trackingNumber ?? '',
  });
  const setPostageField = (num, key, val) =>
    setPostage((p) => ({ ...p, [num]: { ...(p[num] || {}), [key]: val } }));

  const loadAll = useCallback(async () => {
    try {
      // Critical data first - never let a slow secondary call block the orders list.
      const [o, p, s] = await Promise.all([adminGetOrders(), adminGetProducts(), adminGetSettings()]);
      setOrders(o); setProducts(p); setStoreSettings(s); setSettingsDraft(s);
      adminGetContacts().then(setContacts).catch(() => {});
      adminGetEmailLogs().then(setEmails).catch(() => {});
      adminGetDiscounts().then((d) => { setDiscounts(d); setDiscountsLoaded(true); }).catch(() => setDiscountsLoaded(true));
      adminGetPromotions().then(setPromotions).catch(() => {});
      adminGetIncoming().then(setIncoming).catch(() => {});
      adminGetLibraryItems().then(setLibItems).catch(() => {});
      adminGetLibraryStatus().then(setLibStatus).catch(() => {});
      adminGetLibraryAccess().then(setLibAccess).catch(() => {});
      adminGetReviews().then(setReviews).catch(() => {});
      adminGetDiscountUsage().then(setDiscountUsage).catch(() => {});
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem('alp_admin_token');
        navigate('/admin');
      }
    }
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem('alp_admin_token')) { navigate('/admin'); return; }
    loadAll();
    loadEmailStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, loadAll]);

  const logout = () => { localStorage.removeItem('alp_admin_token'); navigate('/admin'); };

  const updateOrder = async (orderNumber, body) => {
    try {
      const updated = await adminUpdateOrder(orderNumber, body);
      setOrders((prev) => prev.map((o) => (o.orderNumber === orderNumber ? updated : o)));
      toast({ title: 'Order updated' });
    } catch (e) { toast({ title: 'Update failed', variant: 'destructive' }); }
  };

  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  const bulkLabels = async (fileList) => {
    const files = Array.from(fileList || []).slice(0, 10);
    if (!files.length) return;
    setBulkBusy(true);
    try {
      const res = await adminBulkShippingLabels(files);
      setBulkResults(res.results);
      toast({ title: `${res.matched} of ${res.results.length} labels matched & saved` });
      adminGetOrders().then(setOrders).catch(() => {});
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Bulk label upload failed', variant: 'destructive' }); }
    finally { setBulkBusy(false); }
  };

  const [labelBusy, setLabelBusy] = useState({});
  const uploadLabel = async (o, file) => {
    if (!file) return;
    setLabelBusy((p) => ({ ...p, [o.orderNumber]: true }));
    try {
      const res = await adminUploadShippingLabel(o.orderNumber, file);
      setPostageField(o.orderNumber, 'carrier', res.carrier);
      setPostageField(o.orderNumber, 'tracking', res.tracking);
      setOrders((prev) => prev.map((x) => (x.orderNumber === o.orderNumber ? { ...x, trackingNumber: res.tracking, carrier: res.carrier } : x)));
      toast({
        title: `Label read — tracking ${res.tracking} saved`,
        description: res.nameMatch === false ? `Heads up: label is addressed to "${res.recipient}" but this order is for ${o.customer?.fullName}.` : undefined,
        variant: res.nameMatch === false ? 'destructive' : undefined,
      });
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Could not read that label', variant: 'destructive' }); }
    finally { setLabelBusy((p) => ({ ...p, [o.orderNumber]: false })); }
  };

  const payCommissionAsCredit = async (u) => {
    const email = window.prompt(`Pay ${u.affiliateName || u.code}'s commission as store credit.\nEnter the affiliate's customer account email:`, u.affiliateEmail || '');
    if (!email || !email.trim()) return;
    const outstanding = (u.commissionOutstanding ?? u.commissionOwed) || 0;
    const raw = window.prompt(`Amount to credit (outstanding: $${outstanding.toFixed(2)}):`, outstanding.toFixed(2));
    if (raw === null) return;
    const amount = parseFloat(raw);
    if (!amount || amount <= 0) { toast({ title: 'Enter a valid amount', variant: 'destructive' }); return; }
    try {
      const res = await adminPayCommissionCredit(u.code, amount, email.trim());
      toast({ title: 'Commission paid as credit', description: `${email.trim()} now has $${res.credit.toFixed(2)} store credit.` });
      adminGetDiscountUsage().then(setDiscountUsage).catch(() => {});
    } catch (e) { toast({ title: 'Payout failed', description: e?.response?.data?.detail || '', variant: 'destructive' }); }
  };

  const attachOrderCode = async (o) => {
    const code = window.prompt(`Attach a discount/affiliate code to ${o.orderNumber}.\nLeave empty to clear.`, o.discountCode || '');
    if (code === null) return;
    const recalc = window.confirm(code.trim()
      ? `Apply ${code.trim().toUpperCase()} to the order totals?\n\nOK = recalculate the discount and new total (use "Resend confirmation" after to email the customer).\nCancel = attach for commission tracking only (totals unchanged).`
      : 'Also remove any discount from the totals and recalculate?\n\nOK = recalculate. Cancel = clear the code only.');
    try {
      const updated = await adminSetOrderDiscountCode(o.orderNumber, code.trim(), recalc);
      setOrders((prev) => prev.map((x) => (x.orderNumber === o.orderNumber ? updated : x)));
      adminGetDiscountUsage().then(setDiscountUsage).catch(() => {});
      toast({
        title: code.trim() ? `Code ${code.trim().toUpperCase()} attached` : 'Code cleared',
        description: recalc
          ? `Discount $${Number(updated.discountAmount || 0).toFixed(2)} applied \u00b7 new total $${Number(updated.total || 0).toFixed(2)}. Use "Resend confirmation" to email the customer.`
          : 'Commission tracking updated. Order totals unchanged.',
      });
    } catch (e) { toast({ title: 'Could not attach code', description: e?.response?.data?.detail || '', variant: 'destructive' }); }
  };

  const toggleItemPreorder = async (o, it) => {
    try {
      const updated = await adminSetItemPreorder(o.orderNumber, it.id, it.size || '', !it.preorder);
      setOrders((prev) => prev.map((x) => (x.orderNumber === o.orderNumber ? updated : x)));
      toast({
        title: !it.preorder ? `${it.name} marked as preorder` : `${it.name} back in this parcel`,
        description: !it.preorder ? 'It ships later - packing slips and the pick list update automatically.' : 'Stock deducted from current inventory.',
      });
    } catch (e) { toast({ title: 'Could not update item', variant: 'destructive' }); }
  };

  const savePostage = async (o) => {
    const p = getPostage(o);
    await updateOrder(o.orderNumber, { carrier: p.carrier, trackingNumber: p.tracking });
    toast({ title: 'Postage saved', description: 'Customer update email queued.' });
  };

  const saveProduct = async () => {
    try {
      let payload = editing;
      if (editing.bundle) {
        const regular = (editing.bundleItems || []).reduce((s, bi) => {
          const p = products.find((x) => x.id === bi.productId);
          return s + (p ? Number(p.price) * Number(bi.qty || 1) : 0);
        }, 0);
        payload = { ...editing, compareAtPrice: editing.compareAtPrice || Math.round(regular * 100) / 100 };
      }
      if (payload.id) {
        const u = await adminUpdateProduct(payload.id, payload);
        setProducts((prev) => prev.map((p) => (p.id === u.id ? u : p)));
      } else {
        const c = await adminCreateProduct(payload);
        setProducts((prev) => [...prev, c]);
      }
      setEditing(null);
      toast({ title: 'Product saved' });
    } catch (e) { toast({ title: 'Save failed', variant: 'destructive' }); }
  };

  const deleteProduct = async (id) => {
    try {
      await adminDeleteProduct(id);
      setProducts((prev) => prev.filter((p) => p.id !== id));
      toast({ title: 'Product deleted' });
    } catch (e) { toast({ title: 'Delete failed', variant: 'destructive' }); }
  };

  const duplicateProduct = (p) => {
    const { id, coaImages, code, ...rest } = p;
    setEditing({ ...EMPTY_PRODUCT, ...rest, coaImages: [], code: '', name: `${p.name} (Copy)` });
    toast({ title: 'Duplicating product', description: 'Review the copy and click Save to create it.' });
  };

  const toggleVisibility = async (p) => {
    const next = !p.hidden;
    try {
      await adminToggleVisibility(p.id, next);
      setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, hidden: next } : x)));
      toast({ title: next ? 'Product hidden' : 'Product visible', description: next ? 'Hidden from the live shop.' : 'Now visible on the live shop.' });
    } catch (e) { toast({ title: 'Update failed', variant: 'destructive' }); }
  };

  const convertSoldout = async () => {
    try {
      const res = await adminSoldoutToPreorder();
      setProducts((prev) => prev.map((p) => (p.stockStatus === 'Out of Stock' ? { ...p, stockStatus: 'Preorder' } : p)));
      toast({ title: 'Switched to Preorder', description: `${res.converted} sold-out product(s) can now be ordered as preorder.` });
    } catch (e) { toast({ title: 'Update failed', variant: 'destructive' }); }
  };

  const saveInternalNotes = async (orderNumber) => {
    try {
      const updated = await adminUpdateOrder(orderNumber, { internalNotes: notesDraft[orderNumber] ?? '' });
      setOrders((prev) => prev.map((o) => (o.orderNumber === orderNumber ? updated : o)));
      toast({ title: 'Internal notes saved' });
    } catch (e) { toast({ title: 'Save failed', variant: 'destructive' }); }
  };

  const openItemEditor = (o) => {
    setItemEditOrder(o);
    setItemEditLines((o.items || []).map((it) => ({ id: it.id, name: it.name, size: it.size, price: it.price, qty: it.qty, digital: it.digital })));
    setItemEditNotify(false);
    setItemEditAddId('');
  };
  const itemLineQty = (idx, delta) => setItemEditLines((p) => p.map((l, i) => i === idx ? { ...l, qty: Math.max(1, (l.qty || 1) + delta) } : l));
  const itemLineSetQty = (idx, val) => setItemEditLines((p) => p.map((l, i) => i === idx ? { ...l, qty: Math.max(1, parseInt(val, 10) || 1) } : l));
  const itemLineRemove = (idx) => setItemEditLines((p) => p.filter((_, i) => i !== idx));
  const itemLineSwap = (idx, newId) => {
    const prod = products.find((x) => x.id === newId);
    if (!prod) return;
    setItemEditLines((p) => p.map((l, i) => i === idx ? { id: prod.id, name: prod.name, size: prod.size, price: prod.price, qty: l.qty, digital: prod.digital } : l));
  };
  const itemAddProduct = () => {
    const prod = products.find((x) => x.id === itemEditAddId);
    if (!prod) return;
    setItemEditLines((p) => {
      const exists = p.findIndex((l) => l.id === prod.id);
      if (exists >= 0) return p.map((l, i) => i === exists ? { ...l, qty: (l.qty || 1) + 1 } : l);
      return [...p, { id: prod.id, name: prod.name, size: prod.size, price: prod.price, qty: 1, digital: prod.digital }];
    });
    setItemEditAddId('');
  };
  const itemEditSubtotal = itemEditLines.reduce((s, l) => s + (Number(l.price) || 0) * (l.qty || 0), 0);
  const saveItemEdits = async () => {
    if (!itemEditOrder || itemEditLines.length === 0) { toast({ title: 'An order needs at least one item', variant: 'destructive' }); return; }
    setItemEditSaving(true);
    try {
      const updated = await adminEditOrderItems(itemEditOrder.orderNumber, itemEditLines.map((l) => ({ id: l.id, qty: l.qty })), itemEditNotify);
      setOrders((prev) => prev.map((o) => (o.orderNumber === updated.orderNumber ? updated : o)));
      toast({ title: 'Order items updated', description: `New total $${Number(updated.total).toFixed(2)}${itemEditOrder.inventoryDeducted ? ' \u00b7 inventory adjusted' : ''}` });
      setItemEditOrder(null);
    } catch (e) { toast({ title: 'Update failed', description: e?.response?.data?.detail || '', variant: 'destructive' }); }
    finally { setItemEditSaving(false); }
  };

  const saveDiscount = async () => {
    const body = {
      ...editingDiscount,
      code: (editingDiscount.code || '').trim().toUpperCase(),
      amount: Number(editingDiscount.amount) || 0,
      minOrder: Number(editingDiscount.minOrder) || 0,
      usageLimit: editingDiscount.usageLimit === '' || editingDiscount.usageLimit == null ? null : Number(editingDiscount.usageLimit),
      expiry: editingDiscount.expiry || null,
      commissionPercent: Number(editingDiscount.commissionPercent) || 0,
      restrictEmail: (editingDiscount.restrictEmail || '').trim().toLowerCase(),
    };
    try {
      if (editingDiscount.id) {
        const u = await adminUpdateDiscount(editingDiscount.id, body);
        setDiscounts((prev) => prev.map((d) => (d.id === u.id ? u : d)));
      } else {
        const c = await adminCreateDiscount(body);
        setDiscounts((prev) => [c, ...prev]);
      }
      setEditingDiscount(null);
      toast({ title: 'Discount saved' });
      adminGetDiscountUsage().then(setDiscountUsage).catch(() => {});
    } catch (e) { toast({ title: 'Save failed', description: e?.response?.data?.detail || '', variant: 'destructive' }); }
  };

  const toggleDiscountActive = async (d) => {
    try {
      const u = await adminUpdateDiscount(d.id, { ...d, active: !d.active });
      setDiscounts((prev) => prev.map((x) => (x.id === u.id ? u : x)));
    } catch (e) { toast({ title: 'Update failed', variant: 'destructive' }); }
  };

  const deleteDiscount = async (id) => {
    const code = (discounts.find((d) => d.id === id) || {}).code || 'this code';
    if (!window.confirm(`Delete ${code}? Customers will no longer be able to use it. This cannot be undone.`)) return;
    try {
      await adminDeleteDiscount(id);
      setDiscounts((prev) => prev.filter((d) => d.id !== id));
      toast({ title: 'Discount deleted' });
    } catch (e) { toast({ title: 'Delete failed', variant: 'destructive' }); }
  };

  const df = (k, v) => setEditingDiscount((p) => ({ ...p, [k]: v }));

  const ef = (k, v) => setEditing((p) => ({ ...p, [k]: v }));
  const [bundlePick, setBundlePick] = useState({ productId: '', qty: 1 });
  const bundleRegularTotal = (editing?.bundleItems || []).reduce((sum, bi) => {
    const p = products.find((x) => x.id === bi.productId);
    return sum + (p ? Number(p.price) * Number(bi.qty || 1) : 0);
  }, 0);
  const addBundleItem = () => {
    if (!bundlePick.productId) { toast({ title: 'Pick a product', variant: 'destructive' }); return; }
    const qty = Math.max(1, parseInt(bundlePick.qty, 10) || 1);
    const items = editing.bundleItems || [];
    const existing = items.find((i) => i.productId === bundlePick.productId);
    const next = existing
      ? items.map((i) => (i.productId === bundlePick.productId ? { ...i, qty: i.qty + qty } : i))
      : [...items, { productId: bundlePick.productId, qty }];
    ef('bundleItems', next);
    setBundlePick({ productId: '', qty: 1 });
  };

  const newOrdersCount = orders.filter((o) => o.orderStatus === 'Ordered' || o.orderStatus === 'Payment Pending').length;
  const packQueueCount = orders.filter((o) => o.orderStatus === 'Payment Confirmed' && (o.items || []).some((it) => !it.digital && (it.qty || 0) > 0)).length;
  const cogsOf = (p) => (Number(p?.supplierCost) || 0) + (Number(p?.extraCost) || 0);

  const setf = (k, v) => setSettingsDraft((p) => ({ ...p, [k]: v }));

  const [aiGenBusy, setAiGenBusy] = useState(false);
  const aiFillProduct = async () => {
    if (!editing?.name?.trim()) { toast({ title: 'Enter the product name first', variant: 'destructive' }); return; }
    setAiGenBusy(true);
    try {
      const r = await adminAiGenerate('product', editing.name, editing.size || '');
      setEditing((p) => ({ ...p, description: r.description || p.description, seoTitle: r.seoTitle || p.seoTitle, seoDescription: r.seoDescription || p.seoDescription }));
      toast({ title: 'Claude wrote the copy', description: 'Review and tweak before saving.' });
    } catch (e) { toast({ title: 'Generation failed', description: e?.response?.data?.detail || '', variant: 'destructive' }); }
    finally { setAiGenBusy(false); }
  };
  const aiFillLibGuide = async () => {
    const prod = products.find((p) => p.id === editingLibItem?.productId);
    const name = (editingLibItem?.title || prod?.name || '').trim();
    if (!name) { toast({ title: 'Set a title or link a product first', variant: 'destructive' }); return; }
    setAiGenBusy(true);
    try {
      const r = await adminAiGenerate('library', name, prod?.size || '');
      setEditingLibItem((p) => ({
        ...p,
        title: p.title || r.title || '',
        whatItIs: r.whatItIs || p.whatItIs, use: r.use || p.use, bacWater: r.bacWater || p.bacWater,
        dosage: r.dosage || p.dosage, timing: r.timing || p.timing, cycle: r.cycle || p.cycle, body: r.body || p.body,
      }));
      toast({ title: 'Claude drafted the guide', description: 'Review every field before saving.' });
    } catch (e) { toast({ title: 'Generation failed', description: e?.response?.data?.detail || '', variant: 'destructive' }); }
    finally { setAiGenBusy(false); }
  };

  const aiFillPromo = async () => {
    if (!editingPromo?.name?.trim()) { toast({ title: 'Enter the promotion name first', variant: 'destructive' }); return; }
    setAiGenBusy(true);
    try {
      const r = await adminAiGenerate('promo', editingPromo.name, '', editingPromo.type || 'General');
      setEditingPromo((p) => ({ ...p, description: r.description || p.description, promoText: r.promoText || p.promoText }));
      toast({ title: 'Claude wrote the promo copy', description: 'Review and tweak before saving.' });
    } catch (e) { toast({ title: 'Generation failed', description: e?.response?.data?.detail || '', variant: 'destructive' }); }
    finally { setAiGenBusy(false); }
  };

  const sendDigestNow = async () => {
    try {
      const r = await adminSendDigestNow();
      toast({
        title: r.status === 'sent' ? `Digest sent to ${r.to}` : r.status === 'logged' ? 'Digest generated (logged only — SMTP not configured here)' : 'Digest failed',
        variant: r.status === 'failed' ? 'destructive' : undefined,
      });
    } catch (e) {
      toast({ title: 'Could not send digest', description: e?.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const saveSettings = async () => {
    try {
      const body = {
        bankAccountName: settingsDraft.bankAccountName,
        bankBSB: settingsDraft.bankBSB,
        bankAccountNumber: settingsDraft.bankAccountNumber,
        bankReferenceText: settingsDraft.bankReferenceText,
        whatsappNumber: settingsDraft.whatsappNumber,
        standardShipping: Number(settingsDraft.standardShipping) || 0,
        expressShipping: Number(settingsDraft.expressShipping) || 0,
        freeShippingThreshold: Number(settingsDraft.freeShippingThreshold) || 0,
        handlingFee: Number(settingsDraft.handlingFee) || 0,
        shippingEnabled: !!settingsDraft.shippingEnabled,
        australiaOnly: !!settingsDraft.australiaOnly,
        checkoutShippingText: settingsDraft.checkoutShippingText,
        bankNameNote: settingsDraft.bankNameNote || '',
        slipNote: settingsDraft.slipNote || '',
        digestEnabled: !!settingsDraft.digestEnabled,
        digestEmail: settingsDraft.digestEmail || '',
        siteGateEnabled: !!settingsDraft.siteGateEnabled,
        cardPaymentEnabled: !!settingsDraft.cardPaymentEnabled,
        cardPaymentsPaused: !!settingsDraft.cardPaymentsPaused,
        cardPaymentLink: settingsDraft.cardPaymentLink || '',
        cardSurchargePercent: Number(settingsDraft.cardSurchargePercent) || 0,
        afterpayEnabled: !!settingsDraft.afterpayEnabled,
        afterpaySurchargePercent: Number(settingsDraft.afterpaySurchargePercent) || 0,
        afterpayFixedFee: Number(settingsDraft.afterpayFixedFee) || 0,
        seoTitle: settingsDraft.seoTitle || '',
        seoDescription: settingsDraft.seoDescription || '',
        seoKeywords: settingsDraft.seoKeywords || '',
        ogTitle: settingsDraft.ogTitle || '',
        ogDescription: settingsDraft.ogDescription || '',
        ogImage: settingsDraft.ogImage || '',
        canonicalUrl: settingsDraft.canonicalUrl || '',
        robotsIndex: settingsDraft.robotsIndex !== false,
        announcementActive: !!settingsDraft.announcementActive,
        announcementText: settingsDraft.announcementText || '',
        announcementButtonText: settingsDraft.announcementButtonText || '',
        announcementButtonLink: settingsDraft.announcementButtonLink || '',
        announcementStart: settingsDraft.announcementStart || '',
        announcementEnd: settingsDraft.announcementEnd || '',
        announcementSpeed: Number(settingsDraft.announcementSpeed) || 2,
        announcementBg: settingsDraft.announcementBg || '#C9A227',
        announcementColor: settingsDraft.announcementColor || '#0A0A0A',
        senderName: settingsDraft.senderName || '',
        senderBusinessName: settingsDraft.senderBusinessName || '',
        senderAddress: settingsDraft.senderAddress || '',
        senderSuburb: settingsDraft.senderSuburb || '',
        senderState: settingsDraft.senderState || '',
        senderPostcode: settingsDraft.senderPostcode || '',
        senderPhone: settingsDraft.senderPhone || '',
        senderEmail: settingsDraft.senderEmail || '',
        deliveryService: settingsDraft.deliveryService || 'EXP',
        parcelDescription: settingsDraft.parcelDescription || 'Nail and Lash Supplies',
        signatureOnDelivery: settingsDraft.signatureOnDelivery || 'NO',
        extraCoverAmount: settingsDraft.extraCoverAmount || '',
        dangerousGoods: settingsDraft.dangerousGoods || 'NO',
        schedule8: settingsDraft.schedule8 || 'NO',
      };
      const s = await adminUpdateSettings(body);
      setStoreSettings(s); setSettingsDraft(s);
      toast({ title: 'Settings saved', description: 'Bank & shipping settings updated.' });
    } catch (e) { toast({ title: 'Save failed', variant: 'destructive' }); }
  };

  const loadEmailStatus = useCallback(async () => {
    setEmailStatusLoading(true);
    try {
      const s = await adminGetEmailStatus();
      setEmailStatus(s);
      if (s.adminOrderEmail && !testEmailTo) setTestEmailTo(s.adminOrderEmail);
    } catch (e) { /* ignore */ }
    finally { setEmailStatusLoading(false); }
  }, [testEmailTo]);

  const sendTest = async () => {
    if (!testEmailTo || !testEmailTo.includes('@')) {
      toast({ title: 'Enter a valid email', variant: 'destructive' });
      return;
    }
    setTestSending(true);
    try {
      const res = await adminSendTestEmail(testEmailTo);
      if (res.ok) toast({ title: 'Test email sent', description: `Delivered to ${testEmailTo}` });
      else toast({ title: 'Test email failed', description: res.info || '', variant: 'destructive' });
      loadEmailStatus();
      loadAll();
    } catch (e) {
      toast({ title: 'Test email failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally { setTestSending(false); }
  };

  const loadPnl = useCallback(async () => {
    setPnlLoading(true);
    try { setPnl(await adminGetProfitLoss(pnlStart, pnlEnd)); }
    catch (e) { /* ignore */ }
    finally { setPnlLoading(false); }
  }, [pnlStart, pnlEnd]);

  const clearPnlDates = useCallback(async () => {
    setPnlStart(''); setPnlEnd('');
    setPnlLoading(true);
    try { setPnl(await adminGetProfitLoss('', '')); }
    catch (e) { /* ignore */ }
    finally { setPnlLoading(false); }
  }, []);

  const pf = (k, v) => setEditingPromo((p) => ({ ...p, [k]: v }));

  const savePromo = async () => {
    if (!editingPromo.name || !editingPromo.name.trim()) {
      toast({ title: 'Promotion name is required', variant: 'destructive' });
      return;
    }
    const body = { ...editingPromo, name: editingPromo.name.trim() };
    try {
      if (editingPromo.id) {
        const u = await adminUpdatePromotion(editingPromo.id, body);
        setPromotions((prev) => prev.map((x) => (x.id === u.id ? u : x)));
      } else {
        const c = await adminCreatePromotion(body);
        setPromotions((prev) => [c, ...prev]);
      }
      setEditingPromo(null);
      toast({ title: 'Promotion saved' });
    } catch (e) { toast({ title: 'Save failed', variant: 'destructive' }); }
  };

  const togglePromo = async (p) => {
    try {
      const u = await adminUpdatePromotion(p.id, { ...p, active: !p.active });
      setPromotions((prev) => prev.map((x) => (x.id === u.id ? u : x)));
    } catch (e) { toast({ title: 'Update failed', variant: 'destructive' }); }
  };

  const deletePromo = async (id) => {
    try {
      await adminDeletePromotion(id);
      setPromotions((prev) => prev.filter((p) => p.id !== id));
      toast({ title: 'Promotion deleted' });
    } catch (e) { toast({ title: 'Delete failed', variant: 'destructive' }); }
  };

  const uploadHero = async (file) => {
    if (!file) return;
    setHeroUploading(true);
    try {
      const res = await adminUploadImage(file);
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${res.url}`;
      const s = await adminUpdateSettings({ heroImage: fullUrl });
      setStoreSettings(s); setSettingsDraft(s);
      toast({ title: 'Homepage image updated' });
    } catch (e) {
      toast({ title: 'Upload failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally { setHeroUploading(false); }
  };

  const uploadOgImage = async (file) => {
    if (!file) return;
    setHeroUploading(true);
    try {
      const res = await adminUploadImage(file);
      setf('ogImage', `${process.env.REACT_APP_BACKEND_URL}${res.url}`);
      toast({ title: 'Social image uploaded', description: 'Remember to Save SEO settings.' });
    } catch (e) {
      toast({ title: 'Upload failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally { setHeroUploading(false); }
  };

  const inf = (k, v) => setEditingIncoming((p) => ({ ...p, [k]: v }));

  const saveIncoming = async () => {
    if (!editingIncoming.name || !editingIncoming.name.trim()) {
      toast({ title: 'Product name is required', variant: 'destructive' });
      return;
    }
    const body = { ...editingIncoming, name: editingIncoming.name.trim() };
    try {
      if (editingIncoming.id) {
        const u = await adminUpdateIncoming(editingIncoming.id, body);
        setIncoming((prev) => prev.map((x) => (x.id === u.id ? u : x)));
      } else {
        const c = await adminCreateIncoming(body);
        setIncoming((prev) => [c, ...prev]);
      }
      setEditingIncoming(null);
      toast({ title: 'Upcoming product saved' });
    } catch (e) { toast({ title: 'Save failed', variant: 'destructive' }); }
  };

  const deleteIncoming = async (id) => {
    try {
      await adminDeleteIncoming(id);
      setIncoming((prev) => prev.filter((x) => x.id !== id));
      toast({ title: 'Removed' });
    } catch (e) { toast({ title: 'Delete failed', variant: 'destructive' }); }
  };

  const rvf = (k, v) => setEditingReview((p) => ({ ...p, [k]: v }));

  const saveReview = async () => {
    if (!editingReview.name || !editingReview.name.trim()) {
      toast({ title: 'Reviewer name is required', variant: 'destructive' });
      return;
    }
    const body = { ...editingReview, name: editingReview.name.trim(), rating: Number(editingReview.rating) || 5, order: Number(editingReview.order) || 0 };
    try {
      if (editingReview.id) {
        const u = await adminUpdateReview(editingReview.id, body);
        setReviews((prev) => prev.map((x) => (x.id === u.id ? u : x)));
      } else {
        const c = await adminCreateReview(body);
        setReviews((prev) => [c, ...prev]);
      }
      setEditingReview(null);
      toast({ title: 'Review saved' });
    } catch (e) { toast({ title: 'Save failed', variant: 'destructive' }); }
  };

  const deleteReview = async (id) => {
    try {
      await adminDeleteReview(id);
      setReviews((prev) => prev.filter((x) => x.id !== id));
      toast({ title: 'Review removed' });
    } catch (e) { toast({ title: 'Delete failed', variant: 'destructive' }); }
  };

  const saveLibPassword = async () => {
    if (!newLibPassword || newLibPassword.length < 4) {
      toast({ title: 'Password must be at least 4 characters', variant: 'destructive' });
      return;
    }
    try {
      await adminSetLibraryPassword(newLibPassword);
      setNewLibPassword('');
      adminGetLibraryStatus().then(setLibStatus).catch(() => {});
      toast({ title: 'Library password updated', description: 'Existing sessions must re-enter the new password.' });
    } catch (e) { toast({ title: 'Failed to set password', variant: 'destructive' }); }
  };

  const lif = (k, v) => setEditingLibItem((p) => ({ ...p, [k]: v }));

  const uploadLibImage = async (file) => {
    if (!file) return;
    setLibImgUploading(true);
    try {
      const res = await adminUploadImage(file);
      lif('image', `${process.env.REACT_APP_BACKEND_URL}${res.url}`);
      toast({ title: 'Image uploaded' });
    } catch (e) { toast({ title: 'Upload failed', variant: 'destructive' }); }
    finally { setLibImgUploading(false); }
  };

  const uploadProductImage = async (file) => {
    if (!file) return;
    setProdImgUploading(true);
    try {
      const res = await adminUploadImage(file);
      ef('image', `${process.env.REACT_APP_BACKEND_URL}${res.url}`);
      toast({ title: 'Image uploaded' });
    } catch (e) { toast({ title: 'Upload failed', description: e?.response?.data?.detail || '', variant: 'destructive' }); }
    finally { setProdImgUploading(false); }
  };

  const uploadCoa = async (file) => {
    if (!file || !editing?.id) return;
    setCoaUploading(true);
    try {
      const res = await adminUploadProductCoa(editing.id, file);
      ef('coaImages', res.coaImages);
      ef('coa', true);
      setProducts((prev) => prev.map((p) => (p.id === editing.id ? { ...p, coaImages: res.coaImages, coa: true } : p)));
      toast({ title: 'COA uploaded', description: `${res.pages} page${res.pages === 1 ? '' : 's'} processed and watermarked.` });
    } catch (e) { toast({ title: 'COA upload failed', description: e?.response?.data?.detail || '', variant: 'destructive' }); }
    finally { setCoaUploading(false); }
  };

  const removeCoa = async () => {
    if (!editing?.id) return;
    try {
      await adminDeleteProductCoa(editing.id);
      ef('coaImages', []);
      setProducts((prev) => prev.map((p) => (p.id === editing.id ? { ...p, coaImages: [] } : p)));
      toast({ title: 'COA removed' });
    } catch (e) { toast({ title: 'Failed to remove COA', variant: 'destructive' }); }
  };

  const openReply = (c) => {
    setReplyingContact(c);
    setReplySubject('Re: Your enquiry \u2014 Australian Luxe Peptides');
    setReplyMessage('');
    setReplyFiles([]);
    setReplyCoaId('');
  };

  const sendReply = async () => {
    if (!replyingContact || !replyMessage.trim()) return;
    setReplySending(true);
    try {
      const res = await adminReplyContact(replyingContact.id, replySubject, replyMessage, replyFiles, replyCoaId);
      setContacts((prev) => prev.map((c) => (c.id === replyingContact.id ? { ...c, replies: [...(c.replies || []), res.reply] } : c)));
      toast({
        title: res.status === 'sent' ? 'Reply sent' : 'Reply logged',
        description: res.status === 'sent' ? `Email delivered to ${replyingContact.email}.` : 'SMTP not configured here \u2014 reply saved and will send once live.',
      });
      setReplyingContact(null);
    } catch (e) {
      toast({ title: 'Failed to send reply', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally { setReplySending(false); }
  };

  const saveLibItem = async () => {
    if (!editingLibItem.title || !editingLibItem.title.trim()) {
      toast({ title: 'Title is required', variant: 'destructive' });
      return;
    }
    const body = { ...editingLibItem, title: editingLibItem.title.trim(), order: Number(editingLibItem.order) || 0 };
    try {
      if (editingLibItem.id) {
        const u = await adminUpdateLibraryItem(editingLibItem.id, body);
        setLibItems((prev) => prev.map((x) => (x.id === u.id ? u : x)));
      } else {
        const c = await adminCreateLibraryItem(body);
        setLibItems((prev) => [...prev, c]);
      }
      setEditingLibItem(null);
      adminGetLibraryStatus().then(setLibStatus).catch(() => {});
      toast({ title: 'Guide saved' });
    } catch (e) { toast({ title: 'Save failed', variant: 'destructive' }); }
  };

  const deleteLibItem = async (id) => {
    try {
      await adminDeleteLibraryItem(id);
      setLibItems((prev) => prev.filter((x) => x.id !== id));
      adminGetLibraryStatus().then(setLibStatus).catch(() => {});
      toast({ title: 'Guide deleted' });
    } catch (e) { toast({ title: 'Delete failed', variant: 'destructive' }); }
  };

  const orderHasLibrary = (o) => (o.items || []).some((i) => i.code === 'LIBRARY-ACCESS' || /library access/i.test(i.name || ''));

  const sendLibraryPassword = async (o) => {
    try {
      const res = await adminSendLibraryPassword(o.orderNumber);
      if (res.ok) toast({ title: 'Password emailed', description: `Sent to ${o.customer?.email}` });
      else toast({ title: 'Email not sent', description: res.info || 'SMTP not configured (logged)', variant: 'destructive' });
      loadAll();
    } catch (e) {
      toast({ title: 'Failed to send', description: e?.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const adjustStock = async (p, inv) => {
    const n = Math.max(0, parseInt(inv, 10) || 0);
    setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, inventory: n } : x)));
    try {
      const updated = await adminSetProductStock(p.id, n);
      setProducts((prev) => prev.map((x) => (x.id === p.id ? updated : x)));
    } catch (e) {
      setProducts((prev) => prev.map((x) => (x.id === p.id ? p : x)));
      toast({ title: 'Stock update failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const toggleOrder = (orderNumber) => setExpandedOrders((p) => ({ ...p, [orderNumber]: !p[orderNumber] }));

  const openCustEditor = (o) => {
    const c = o.customer || {};
    setCustDraft({ fullName: c.fullName || '', email: c.email || '', phone: c.phone || '', address: c.address || '', city: c.city || '', state: c.state || '', postcode: c.postcode || '', notes: c.notes || '' });
    setCustEditOrder(o);
  };

  const saveCustEdit = async () => {
    if (!custEditOrder) return;
    setCustSaving(true);
    try {
      const updated = await adminEditOrderCustomer(custEditOrder.orderNumber, custDraft);
      setOrders((prev) => prev.map((x) => (x.orderNumber === updated.orderNumber ? updated : x)));
      setCustEditOrder(null);
      toast({ title: 'Customer details updated', description: `Order ${updated.orderNumber}` });
    } catch (e) {
      toast({ title: 'Update failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally { setCustSaving(false); }
  };

  const changePaymentMethod = async (o, method) => {
    if (method === (o.paymentMethod || 'Bank Transfer')) return;
    try {
      const updated = await adminSetPaymentMethod(o.orderNumber, method, false);
      setOrders((prev) => prev.map((x) => (x.orderNumber === o.orderNumber ? updated : x)));
      const fee = Number(updated.cardSurcharge || 0);
      toast({ title: `Payment method: ${method}`, description: `Fee ${fee > 0 ? `$${fee.toFixed(2)}` : 'none'} \u00b7 New total $${Number(updated.total).toFixed(2)}. Use "Resend confirmation" to email the customer.` });
    } catch (e) {
      toast({ title: 'Failed to change payment method', description: e?.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const resendConfirmation = async (o) => {
    try {
      const res = await adminResendConfirmation(o.orderNumber);
      if (res.ok) toast({ title: 'Confirmation resent', description: `Sent to ${o.customer?.email}` });
      else toast({ title: res.configured ? 'Email not sent' : 'Email logged (SMTP not configured)', description: res.info || '', variant: res.configured ? 'destructive' : 'default' });
      setOrders((prev) => prev.map((x) => (x.orderNumber === o.orderNumber ? { ...x, customerEmailStatus: res.status } : x)));
    } catch (e) {
      toast({ title: 'Failed to resend confirmation', description: e?.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const sendReviewRequest = async (o) => {
    try {
      const res = await adminSendReviewRequest(o.orderNumber);
      if (res.ok) toast({ title: 'Review request sent', description: `Sent to ${o.customer?.email}` });
      else toast({ title: res.configured ? 'Email not sent' : 'Email logged (SMTP not configured)', description: res.info || '', variant: res.configured ? 'destructive' : 'default' });
      setOrders((prev) => prev.map((x) => (x.orderNumber === o.orderNumber ? { ...x, reviewRequestSentAt: res.sentAt } : x)));
    } catch (e) {
      toast({ title: 'Failed to send review request', description: e?.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const addAccess = async () => {
    if (!newAccessEmail || !newAccessEmail.includes('@')) {
      toast({ title: 'Enter a valid email', variant: 'destructive' });
      return;
    }
    try {
      const g = await adminAddLibraryAccess(newAccessEmail.trim(), '');
      setLibAccess((prev) => [g, ...prev.filter((x) => x.id !== g.id)]);
      setNewAccessEmail('');
      toast({ title: 'Access granted', description: g.email });
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail || '', variant: 'destructive' }); }
  };

  const toggleAccess = async (g) => {
    const next = g.status === 'active' ? 'disabled' : 'active';
    try {
      const u = await adminSetLibraryAccessStatus(g.id, next);
      setLibAccess((prev) => prev.map((x) => (x.id === u.id ? u : x)));
      toast({ title: next === 'disabled' ? 'Access disabled' : 'Access enabled', description: g.email });
    } catch (e) { toast({ title: 'Update failed', variant: 'destructive' }); }
  };

  const removeAccess = async (id) => {
    try {
      await adminDeleteLibraryAccess(id);
      setLibAccess((prev) => prev.filter((x) => x.id !== id));
      toast({ title: 'Access removed' });
    } catch (e) { toast({ title: 'Delete failed', variant: 'destructive' }); }
  };

  const pnlOrders = pnlOrdersFor
    ? orders.filter((o) => (o.items || []).some((it) => it.id === pnlOrdersFor.id || it.name === pnlOrdersFor.name))
    : [];

  const selectTab = (v) => { setActiveTab(v); if (v === 'pnl') loadPnl(); };
  const selectSection = (g) => { setActiveSection(g.key); selectTab(g.tabs[0]); };
  const goTo = (sectionKey, tabKey) => { setActiveSection(sectionKey); selectTab(tabKey); };
  const tabBadge = (key) => {
    if (key === 'orders' && newOrdersCount > 0) return <span data-testid="new-orders-badge" className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold">{newOrdersCount}</span>;
    if (key === 'fulfillment' && packQueueCount > 0) return <span data-testid="pack-queue-badge" className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-alp-gold text-alp-black text-[10px] font-bold">{packQueueCount}</span>;
    return null;
  };

  return (
    <div className="min-h-screen bg-alp-black admin-luxe">
      <header className="border-b border-alp-line bg-alp-dark/60">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 md:py-0 md:h-20 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5 md:gap-3 min-w-0">
            <Logo size={36} />
            <div className="min-w-0">
              <div className="font-serif text-alp-cream text-base md:text-lg leading-tight whitespace-nowrap">Admin Dashboard</div>
              <div className="hidden sm:block text-alp-gold text-[10px] tracking-[0.3em] uppercase">Australian Luxe Peptides</div>
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-3 shrink-0">
            <button onClick={() => navigate('/')} className="inline-flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase text-alp-gray hover:text-alp-gold transition-colors"><ChevronLeft size={14} /> <span className="hidden sm:inline">Store</span></button>
            <button onClick={logout} className="inline-flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase text-alp-cream border border-alp-line px-3 md:px-4 py-2 rounded hover:border-alp-gold hover:text-alp-gold transition-colors"><LogOut size={14} /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-[1440px] mx-auto px-5 md:px-8 py-8">
        <Tabs value={activeTab} onValueChange={selectTab}>
          <div className="flex flex-col lg:flex-row gap-5 lg:gap-8 items-start">
            {/* Mobile navigation: horizontal chip rows */}
            <div className="lg:hidden w-full" data-testid="admin-mobile-nav">
              <div className="flex gap-2 overflow-x-auto pb-1.5 -mx-5 px-5" style={{ scrollbarWidth: 'none' }}>
                {TAB_GROUPS.map((g) => {
                  const GIcon = g.icon;
                  const active = g.key === activeSection;
                  return (
                    <button key={g.key} data-testid={`m-section-${g.key}`} onClick={() => selectSection(g)}
                      className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[11px] tracking-[0.12em] uppercase font-semibold border transition-colors ${active ? 'bg-alp-gold text-alp-black border-alp-gold' : 'bg-alp-card/60 text-alp-cream border-alp-line'}`}>
                      <GIcon size={13} /> {g.label}
                    </button>
                  );
                })}
              </div>
              {(() => {
                const ag = TAB_GROUPS.find((g) => g.key === activeSection);
                if (!ag || ag.tabs.length < 2) return null;
                return (
                  <TabsList className="bg-transparent border-0 p-0 mt-1.5 flex gap-1.5 overflow-x-auto h-auto w-full justify-start -mx-5 px-5" style={{ scrollbarWidth: 'none' }}>
                    {ag.tabs.map((key) => {
                      const t = TABS[key];
                      const TIcon = t.icon;
                      return (
                        <TabsTrigger key={key} value={key}
                          className="shrink-0 px-3 py-2 rounded-full text-alp-cream/75 text-xs border border-alp-line data-[state=active]:bg-alp-gold/15 data-[state=active]:text-alp-gold data-[state=active]:border-alp-gold">
                          <TIcon size={13} className="mr-1.5" /> {t.label}{tabBadge(key)}
                        </TabsTrigger>
                      );
                    })}
                  </TabsList>
                );
              })()}
            </div>

            {/* Sidebar navigation */}
            <aside className="hidden lg:block w-full lg:w-60 shrink-0 lg:sticky lg:top-6" data-testid="admin-sidebar">
              <nav className="space-y-1.5" data-testid="admin-sections">
                {TAB_GROUPS.map((g) => {
                  const GIcon = g.icon;
                  const active = g.key === activeSection;
                  return (
                    <div key={g.key}>
                      <button
                        data-testid={`section-${g.key}`}
                        onClick={() => selectSection(g)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-xs tracking-[0.15em] uppercase font-semibold transition-colors border text-left ${active ? 'bg-alp-gold text-alp-black border-alp-gold' : 'bg-alp-card/60 text-alp-cream border-alp-line hover:border-alp-gold/60'}`}
                      >
                        <GIcon size={15} /> {g.label}
                      </button>
                      {active && g.tabs.length > 1 && (
                        <TabsList className="bg-transparent border-0 p-0 mt-1.5 mb-2 flex flex-col items-stretch h-auto w-full gap-0.5">
                          {g.tabs.map((key) => {
                            const t = TABS[key];
                            const TIcon = t.icon;
                            return (
                              <TabsTrigger
                                key={key}
                                value={key}
                                data-testid={t.testid}
                                className="justify-start w-full pl-6 pr-3 py-2.5 rounded-md text-alp-cream/75 text-sm border-l-2 border-transparent data-[state=active]:bg-alp-gold/15 data-[state=active]:text-alp-gold data-[state=active]:border-alp-gold"
                              >
                                <TIcon size={14} className="mr-2" /> {t.label}{tabBadge(key)}
                              </TabsTrigger>
                            );
                          })}
                        </TabsList>
                      )}
                    </div>
                  );
                })}
              </nav>
            </aside>

            {/* Main content */}
            <main className="flex-1 min-w-0 w-full">

          {/* OVERVIEW */}
          <TabsContent value="overview" className="mt-0">
            <OverviewPanel goTo={goTo} />
          </TabsContent>

          {/* STOCK TAKE & FINANCE */}
          <TabsContent value="stock" className="mt-0">
            <TabInsights tab="stock" />
            <StockPanel />
          </TabsContent>
          <TabsContent value="fulfillment" className="mt-0">
            <TabInsights tab="fulfillment" />
            <FulfillmentPanel onOrderShipped={(updated) => setOrders((prev) => prev.map((o) => (o.orderNumber === updated.orderNumber ? updated : o)))} />
          </TabsContent>
          <TabsContent value="payroll" className="mt-0">
            <PayrollPanel />
          </TabsContent>

          {/* ORDERS */}
          <TabsContent value="orders" className="mt-0">
            <TabInsights tab="orders" />
            <div className="flex flex-wrap gap-3 mb-6">
              <Input value={orderSearch} onChange={(e) => setOrderSearch(e.target.value)} placeholder="Search name, order #, email, product, date" data-testid="order-search" className="bg-alp-dark border-alp-line text-alp-cream flex-1 min-w-[220px]" />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="order-status-filter" className="bg-alp-dark border-alp-line text-alp-cream w-56"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-alp-card border-alp-line text-alp-cream">
                  <SelectItem value="All">All statuses</SelectItem>
                  {ORDER_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <label data-testid="bulk-label-upload" title="Upload several AusPost labels at once - each is matched to its order by the recipient name" className="inline-flex items-center gap-1.5 px-4 h-10 border border-alp-gold/50 text-alp-gold text-[11px] tracking-[0.12em] uppercase rounded cursor-pointer hover:bg-alp-gold/10 whitespace-nowrap">
                {bulkBusy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {bulkBusy ? 'Reading labels…' : 'Bulk labels'}
                <input type="file" multiple accept="image/*,.pdf,.heic" className="hidden" onChange={(e) => { bulkLabels(e.target.files); e.target.value = ''; }} />
              </label>
            </div>
            {bulkResults && (
              <div className="border border-alp-line rounded-lg bg-alp-card/40 p-4 mb-6" data-testid="bulk-label-results">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-alp-gold">Bulk label results</span>
                  <button onClick={() => setBulkResults(null)} data-testid="bulk-label-results-close" className="text-alp-gray hover:text-alp-cream"><X size={14} /></button>
                </div>
                {bulkResults.map((r, i) => (
                  <div key={i} className="text-xs py-1.5 flex flex-wrap gap-x-2 items-center border-t border-alp-line/40 first:border-0">
                    {r.ok ? <CheckCircle2 size={13} className="text-emerald-400 shrink-0" /> : <XCircle size={13} className="text-red-400 shrink-0" />}
                    <span className="text-alp-cream">{r.file}</span>
                    {r.ok ? (
                      <span className="text-alp-gray">&rarr; {r.orderNumber} ({r.customer}) &middot; {r.tracking}</span>
                    ) : (
                      <span className="text-red-300">{r.reason}{r.tracking ? ` · tracking read: ${r.tracking}` : ''}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
            {(() => {
              const q = orderSearch.trim().toLowerCase();
              const filtered = orders.filter((o) => {
                if (statusFilter !== 'All' && o.orderStatus !== statusFilter) return false;
                if (!q) return true;
                const hay = [o.orderNumber, o.customer?.fullName, o.customer?.email, o.createdAt,
                  (o.items || []).map((i) => i.name).join(' '), o.paymentStatus, o.orderStatus].join(' ').toLowerCase();
                return hay.includes(q);
              });
              return filtered.length === 0 ? (
                <p className="text-alp-gray py-16 text-center">No orders match. Completed checkouts will appear here.</p>
              ) : (
              <>
              <p className="lg:hidden text-[10px] tracking-[0.1em] uppercase text-alp-gray/70 mb-2">Tip: swipe an order right to mark it paid or packed</p>
              <div className="border border-alp-line rounded-lg overflow-hidden divide-y divide-alp-line/60 bg-alp-card/30">
                {filtered.slice(0, ordersShown).map((o) => {
                  const open = !!expandedOrders[o.orderNumber];
                  const itemCount = (o.items || []).reduce((s, i) => s + (i.qty || 0), 0);
                  const STATUS_PILL = {
                    'Ordered': 'text-alp-gray border-alp-line',
                    'Payment Pending': 'text-amber-300 border-amber-500/50 bg-amber-500/10',
                    'Payment Confirmed': 'text-alp-gold border-alp-gold/50 bg-alp-gold/10',
                    'Packed': 'text-sky-300 border-sky-400/50 bg-sky-400/10',
                    'Shipped': 'text-emerald-300 border-emerald-400/50 bg-emerald-400/10',
                    'Delivered': 'text-emerald-400 border-emerald-500/60 bg-emerald-500/10',
                    'Cancelled': 'text-red-400 border-red-500/50 bg-red-500/10',
                  };
                  const paid = o.paymentStatus === 'Paid';
                  const swipeAction = !CAN_SWIPE || o.orderStatus === 'Cancelled' ? null
                    : !paid ? { label: 'Mark paid', bg: 'bg-emerald-500/25', fg: 'text-emerald-300', body: { orderStatus: 'Payment Confirmed' }, testid: `swipe-row-${o.orderNumber}` }
                    : o.orderStatus === 'Payment Confirmed' ? { label: 'Mark packed', bg: 'bg-sky-500/25', fg: 'text-sky-300', body: { orderStatus: 'Packed' }, testid: `swipe-row-${o.orderNumber}` }
                    : null;
                  return (
                  <div key={o.orderNumber} data-testid={`order-${o.orderNumber}`} className={`${open ? 'bg-alp-dark/40' : ''} border-l-2 ${o.orderStatus === 'Cancelled' ? 'border-l-red-500/40' : paid ? 'border-l-emerald-400/70' : 'border-l-amber-400/80'}`}>
                    <SwipeRow action={swipeAction} onAction={() => updateOrder(o.orderNumber, swipeAction.body)}>
                    <button
                      onClick={() => toggleOrder(o.orderNumber)}
                      data-testid={`order-row-${o.orderNumber}`}
                      className="w-full grid grid-cols-[auto_1fr_auto] sm:grid-cols-[130px_1fr_auto_auto_auto_24px] items-center gap-x-4 gap-y-1 px-4 py-3 text-left hover:bg-alp-gold/5 transition-colors"
                    >
                      <span className="min-w-0">
                        <span className="block text-alp-gold text-sm tracking-[0.1em]">{o.orderNumber}</span>
                        <span className="block text-alp-gray text-[11px]">{o.createdAt ? new Date(o.createdAt).toLocaleDateString() : '\u2014'}</span>
                        {o.orderStatus !== 'Cancelled' && (
                          <span data-testid={`paid-pill-${o.orderNumber}`} className={`inline-flex items-center gap-1 mt-1 text-[9px] tracking-[0.12em] uppercase rounded-full px-2 py-0.5 border ${paid ? 'text-emerald-300 border-emerald-400/50 bg-emerald-400/10' : 'text-amber-300 border-amber-500/60 bg-amber-500/15'}`}>{paid ? '\u2713 Paid' : '\u25CF Awaiting payment'}</span>
                        )}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-alp-cream text-sm truncate">{o.customer?.fullName}</span>
                        <span className="block text-alp-gray text-[11px] truncate">{itemCount} item{itemCount === 1 ? '' : 's'} &middot; {o.customer?.state || ''}</span>
                      </span>
                      <span className="hidden sm:inline-flex">
                        {o.paymentMethod === 'Card' || o.paymentMethod === 'Afterpay' ? (
                          <span className={`inline-flex items-center gap-1 text-[10px] tracking-[0.1em] uppercase rounded px-2 py-0.5 border ${o.paymentMethod === 'Afterpay' ? 'text-purple-300 border-purple-400/50 bg-purple-400/10' : 'text-sky-300 border-sky-400/50 bg-sky-400/10'}`}><CreditCard size={10} /> {o.paymentMethod}</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.1em] uppercase text-amber-300 border border-amber-500/50 bg-amber-500/10 rounded px-2 py-0.5"><Landmark size={10} /> Bank</span>
                        )}
                      </span>
                      <span className={`hidden sm:inline-flex items-center text-[10px] tracking-[0.1em] uppercase rounded-full px-2.5 py-0.5 border ${STATUS_PILL[o.orderStatus] || 'text-alp-gray border-alp-line'}`}>{o.orderStatus}</span>
                      <span className="text-alp-cream text-sm text-right whitespace-nowrap">${Number(o.total || 0).toFixed(2)}</span>
                      <ChevronDown size={15} className={`text-alp-gray transition-transform ${open ? 'rotate-180 text-alp-gold' : ''}`} />
                    </button>
                    </SwipeRow>
                    {open && (
                    <div className="px-5 pb-6 pt-2 border-t border-alp-line/40">
                    <div className="flex flex-wrap justify-between gap-4 mb-4">
                      <div>
                        <div className="text-alp-gold tracking-[0.12em]">{o.orderNumber}</div>
                        <div className="text-alp-cream">{o.customer.fullName} &middot; {o.customer.email} &middot; {o.customer.phone}</div>
                        <div className="text-alp-gray text-sm">{o.customer.address}, {o.customer.city} {o.customer.state} {o.customer.postcode}</div>
                        <div className="text-alp-gray text-xs mt-1">Placed: {o.createdAt ? new Date(o.createdAt).toLocaleString() : '\u2014'}</div>
                        <button
                          onClick={() => { if (!printPackingSlip(o, settingsDraft?.slipNote || '')) toast({ title: 'Popup blocked', description: 'Allow popups for this site to print slips.', variant: 'destructive' }); }}
                          data-testid={`order-print-slip-${o.orderNumber}`}
                          title="Prints right here - you stay on this page"
                          className="inline-flex items-center gap-1.5 mt-2 mr-2 text-[10px] tracking-[0.12em] uppercase border border-alp-gold/50 text-alp-gold rounded px-2.5 py-1 hover:bg-alp-gold hover:text-alp-black transition-colors"
                        >
                          <Printer size={11} /> Print Slip
                        </button>
                        <button
                          onClick={() => openCustEditor(o)}
                          data-testid={`edit-customer-${o.orderNumber}`}
                          className="inline-flex items-center gap-1.5 mt-2 mr-2 text-[10px] tracking-[0.12em] uppercase border border-alp-line text-alp-cream rounded px-2.5 py-1 hover:border-alp-gold hover:text-alp-gold transition-colors"
                        >
                          <Pencil size={11} /> Edit Customer
                        </button>
                        {o.customer.notes ? <div className="text-alp-gray text-xs mt-1">Customer notes: {o.customer.notes}</div> : null}
                        {o.auspostExportedAt ? <div className="inline-flex items-center gap-1.5 mt-2 text-[10px] tracking-[0.12em] uppercase text-emerald-400 border border-emerald-400/40 rounded px-2 py-0.5" data-testid={`exported-badge-${o.orderNumber}`}><Download size={11} /> AusPost exported {new Date(o.auspostExportedAt).toLocaleDateString()}</div> : null}
                      </div>
                      <div className="text-right">
                        <div className="text-alp-gold text-xl">${o.total.toFixed(2)} AUD</div>
                        <div className="flex items-center justify-end gap-2 mt-1">
                          {o.paymentMethod === 'Card' || o.paymentMethod === 'Afterpay' ? (
                            <span data-testid={`pay-badge-${o.orderNumber}`} className={`inline-flex items-center gap-1 text-[10px] tracking-[0.12em] uppercase rounded px-2 py-0.5 ${o.paymentMethod === 'Afterpay' ? 'text-purple-300 border border-purple-400/50 bg-purple-400/10' : 'text-sky-300 border border-sky-400/50 bg-sky-400/10'}`}><CreditCard size={11} /> {o.paymentMethod === 'Afterpay' ? 'Afterpay' : 'Card'}{Number(o.cardSurcharge || 0) > 0 ? ` +$${Number(o.cardSurcharge).toFixed(2)}` : ''}</span>
                          ) : (
                            <span data-testid={`pay-badge-${o.orderNumber}`} className="inline-flex items-center gap-1 text-[10px] tracking-[0.12em] uppercase text-amber-300 border border-amber-500/50 bg-amber-500/10 rounded px-2 py-0.5"><Landmark size={11} /> Bank</span>
                          )}
                          <span className={`text-xs font-semibold ${paid ? 'text-emerald-300' : 'text-amber-300'}`}>{o.paymentStatus}</span>
                        </div>
                        <div className="text-alp-gray text-xs">Type: {o.orderType || 'In Stock'}</div>
                        {o.discountCode ? <div className="text-alp-gold/90 text-xs">Code: {o.discountCode} (&minus;${Number(o.discountAmount || 0).toFixed(2)}) <button onClick={() => attachOrderCode(o)} data-testid={`order-attach-code-${o.orderNumber}`} className="underline underline-offset-2 text-alp-gray hover:text-alp-gold ml-1">change</button></div> : <button onClick={() => attachOrderCode(o)} data-testid={`order-attach-code-${o.orderNumber}`} className="text-[10px] tracking-[0.1em] uppercase text-alp-gray hover:text-alp-gold underline underline-offset-2">+ Attach code</button>}
                        {Number(o.creditUsed || 0) > 0 ? <div className="text-emerald-300 text-xs">Store credit used: &minus;${Number(o.creditUsed).toFixed(2)}</div> : null}
                        <div className="text-alp-gray text-[11px] mt-1">Sub ${Number(o.subtotal || 0).toFixed(2)} &middot; Ship {o.shipping === 0 ? 'FREE' : `$${Number(o.shipping || 0).toFixed(2)}`}</div>
                        <div className="text-alp-gray text-[11px]">Email: cust {o.customerEmailStatus || '\u2014'} / admin {o.adminEmailStatus || '\u2014'}</div>
                        {o.orderStatus !== 'Cancelled' && (
                          <div className="flex items-center justify-end gap-2 mt-2">
                            <Select value={o.paymentMethod || 'Bank Transfer'} onValueChange={(v) => changePaymentMethod(o, v)}>
                              <SelectTrigger data-testid={`payment-method-select-${o.orderNumber}`} className="bg-alp-dark border-alp-line text-alp-cream h-7 w-36 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-alp-card border-alp-line text-alp-cream">
                                <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                                <SelectItem value="Card">Card</SelectItem>
                                <SelectItem value="Afterpay">Afterpay</SelectItem>
                              </SelectContent>
                            </Select>
                            <button onClick={() => resendConfirmation(o)} data-testid={`resend-confirmation-${o.orderNumber}`} title="Re-email the order confirmation with current totals and payment instructions" className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.1em] uppercase border border-alp-gold/50 text-alp-gold px-2.5 py-1 rounded hover:bg-alp-gold hover:text-alp-black transition-colors">
                              <Mail size={11} /> Resend confirmation
                            </button>
                          </div>
                        )}
                        <a href={whatsappLink(o)} target="_blank" rel="noopener noreferrer" data-testid={`whatsapp-confirm-${o.orderNumber}`} className="inline-flex items-center gap-1.5 mt-2 text-[11px] tracking-[0.1em] uppercase border border-emerald-400/40 text-emerald-300/90 px-3 py-1.5 rounded hover:border-emerald-400 transition-colors">
                          <MessageCircle size={13} /> Send WhatsApp Confirmation
                        </a>
                        {orderHasLibrary(o) && (
                          <button onClick={() => sendLibraryPassword(o)} data-testid={`send-library-password-${o.orderNumber}`} className="inline-flex items-center gap-1.5 mt-2 ml-2 text-[11px] tracking-[0.1em] uppercase border border-alp-gold/50 text-alp-gold px-3 py-1.5 rounded hover:bg-alp-gold hover:text-alp-black transition-colors">
                            <BookOpen size={13} /> Send Library Password
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="text-alp-gray text-sm mb-4 flex items-start justify-between gap-3">
                      <span className="flex-1">
                        {o.items.map((i, ix) => (
                          <span key={ix} className="inline-flex items-center flex-wrap gap-1">
                            <span>{ix > 0 ? ' \u00b7 ' : ''}{i.name} ({i.size}) &times;{i.qty} @ ${Number(i.price).toFixed(2)}</span>
                            {!i.digital && o.orderStatus !== 'Cancelled' && (
                              <button
                                onClick={() => toggleItemPreorder(o, i)}
                                data-testid={`order-item-preorder-${o.orderNumber}-${i.id}`}
                                title={i.preorder ? 'Marked preorder - ships later. Click to put back in this parcel.' : 'Click to mark as preorder (ships later)'}
                                className={`inline-flex items-center gap-1 text-[10px] tracking-[0.1em] uppercase rounded px-2 py-0.5 border transition-colors ${i.preorder ? 'text-amber-300 border-amber-500/50 bg-amber-500/10 hover:border-amber-400' : 'text-alp-gray/80 border-alp-line hover:text-amber-300 hover:border-amber-500/50'}`}
                              >
                                <Clock size={10} /> {i.preorder ? 'Preorder \u2014 ships later' : 'Mark preorder'}
                              </button>
                            )}
                          </span>
                        ))}
                        {(o.freebies || []).length > 0 && (
                          <span className="block text-emerald-300/90 text-xs mt-1" data-testid={`order-freebies-${o.orderNumber}`}>
                            Freebies: {(o.freebies || []).map((f) => `${f.name}${f.size ? ` (${f.size})` : ''} \u00d7${f.qty}`).join(' \u00b7 ')}
                          </span>
                        )}
                      </span>
                      <button onClick={() => openItemEditor(o)} data-testid={`edit-items-${o.orderNumber}`} className={`shrink-0 inline-flex items-center gap-1.5 border border-alp-gold/50 text-alp-gold px-3 py-1.5 rounded text-[10px] tracking-[0.12em] uppercase hover:bg-alp-gold hover:text-alp-black transition-colors ${o.orderStatus === 'Cancelled' ? 'hidden' : ''}`}>
                        <Pencil size={12} /> Edit items
                      </button>
                    </div>
                    <div className="flex flex-wrap items-end gap-4">
                      <div className="w-52">
                        <Label className="text-[10px] tracking-[0.15em] uppercase text-alp-gray mb-1 block">Order Status</Label>
                        <Select value={o.orderStatus} onValueChange={(v) => updateOrder(o.orderNumber, { orderStatus: v, notifyCustomer: notifyMap[o.orderNumber] !== false })}>
                          <SelectTrigger className="bg-alp-dark border-alp-line text-alp-cream h-9"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-alp-card border-alp-line text-alp-cream">
                            {ORDER_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <label className="flex items-center gap-2 text-[11px] text-alp-cream/80 mt-2 cursor-pointer">
                          <input type="checkbox" checked={notifyMap[o.orderNumber] !== false} onChange={(e) => setNotifyMap((p) => ({ ...p, [o.orderNumber]: e.target.checked }))} data-testid={`notify-customer-${o.orderNumber}`} className="accent-alp-gold" />
                          Email customer on status change
                        </label>
                      </div>
                      <div className="w-44">
                        <Label className="text-[10px] tracking-[0.15em] uppercase text-alp-gray mb-1 block">Carrier</Label>
                        <Select value={getPostage(o).carrier} onValueChange={(v) => setPostageField(o.orderNumber, 'carrier', v)}>
                          <SelectTrigger className="bg-alp-dark border-alp-line text-alp-cream h-9"><SelectValue placeholder="Select carrier" /></SelectTrigger>
                          <SelectContent className="bg-alp-card border-alp-line text-alp-cream">
                            {CARRIERS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex-1 min-w-[200px]">
                        <Label className="text-[10px] tracking-[0.15em] uppercase text-alp-gray mb-1 block">Tracking Number</Label>
                        <div className="flex gap-2">
                          <Input value={getPostage(o).tracking} onChange={(e) => setPostageField(o.orderNumber, 'tracking', e.target.value)} className="bg-alp-dark border-alp-line text-alp-cream h-9" />
                          <button onClick={() => savePostage(o)} className="px-4 h-9 bg-alp-gold text-alp-black text-xs tracking-[0.15em] uppercase font-semibold hover:bg-alp-goldlight transition-colors">Save</button>
                          <label data-testid={`label-upload-${o.orderNumber}`} title="Upload the AusPost label photo/PDF - AI reads the tracking number and fills it in" className="inline-flex items-center gap-1.5 px-3 h-9 border border-alp-gold/50 text-alp-gold text-[10px] tracking-[0.12em] uppercase rounded cursor-pointer hover:bg-alp-gold/10 whitespace-nowrap">
                            {labelBusy[o.orderNumber] ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} {labelBusy[o.orderNumber] ? 'Reading…' : 'Label'}
                            <input type="file" accept="image/*,.pdf,.heic" className="hidden" onChange={(e) => { uploadLabel(o, e.target.files?.[0]); e.target.value = ''; }} />
                          </label>
                        </div>
                      </div>
                    </div>

                    {(o.trackingNumber || o.carrier) && (
                      <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-alp-line/60">
                        {trackingUrl(o.carrier, o.trackingNumber) ? (
                          <a href={trackingUrl(o.carrier, o.trackingNumber)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-xs text-alp-gold hover:text-alp-goldlight transition-colors">
                            <ExternalLink size={14} /> Track parcel ({o.carrier}: {o.trackingNumber})
                          </a>
                        ) : (
                          <span className="text-xs text-alp-gray">{o.carrier || 'Carrier'}: {o.trackingNumber || '\u2014'}</span>
                        )}
                        <a href={whatsappLink(o)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-xs tracking-[0.12em] uppercase border border-emerald-400/40 text-emerald-300/90 px-4 py-2 rounded hover:border-emerald-400 transition-colors">
                          <MessageCircle size={14} /> Send tracking via WhatsApp
                        </a>
                        <a href={smsLink(o)} data-testid={`sms-tracking-${o.orderNumber}`} title="Opens your phone's messaging app with the tracking text pre-filled - for customers without WhatsApp" className="inline-flex items-center gap-2 text-xs tracking-[0.12em] uppercase border border-sky-400/40 text-sky-300/90 px-4 py-2 rounded hover:border-sky-400 transition-colors">
                          <MessageSquare size={14} /> Send tracking via SMS
                        </a>
                      </div>
                    )}

                    <div className="mt-4 pt-4 border-t border-alp-line/60 flex flex-wrap items-center gap-3">
                      <button onClick={() => sendReviewRequest(o)} data-testid={`send-review-request-${o.orderNumber}`} className="inline-flex items-center gap-2 text-xs tracking-[0.12em] uppercase border border-alp-gold/50 text-alp-gold px-4 py-2 rounded hover:bg-alp-gold hover:text-alp-black transition-colors">
                        <Star size={14} /> Send review request
                      </button>
                      {isReviewDue(o) && (
                        <a href={reviewWhatsappLink(o)} target="_blank" rel="noopener noreferrer" data-testid={`review-whatsapp-${o.orderNumber}`} className="inline-flex items-center gap-2 text-xs tracking-[0.12em] uppercase border border-emerald-400/40 text-emerald-300/90 px-4 py-2 rounded hover:border-emerald-400 transition-colors">
                          <MessageCircle size={14} /> Review request via WhatsApp
                        </a>
                      )}
                      {isReviewDue(o) && !o.reviewRequestSentAt && (
                        <span data-testid={`review-due-badge-${o.orderNumber}`} className="text-[11px] tracking-[0.1em] uppercase text-emerald-300/90 border border-emerald-400/40 rounded px-2.5 py-1 inline-flex items-center gap-1">
                          <Clock size={12} /> Due for review ({daysSincePlaced(o)}d)
                        </span>
                      )}
                      {o.reviewRequestSentAt && (
                        <span className="text-[11px] text-alp-gray inline-flex items-center gap-1"><CheckCircle2 size={13} className="text-alp-gold" /> Review request sent {new Date(o.reviewRequestSentAt).toLocaleDateString()}</span>
                      )}
                    </div>

                    <div className="mt-4 pt-4 border-t border-alp-line/60">
                      <Label className="text-[10px] tracking-[0.15em] uppercase text-alp-gray mb-1 block">Internal Notes (admin only)</Label>
                      <div className="flex gap-2">
                        <Input
                          value={notesDraft[o.orderNumber] ?? o.internalNotes ?? ''}
                          onChange={(e) => setNotesDraft((p) => ({ ...p, [o.orderNumber]: e.target.value }))}
                          placeholder="Private notes about this order"
                          data-testid={`internal-notes-${o.orderNumber}`}
                          className="bg-alp-dark border-alp-line text-alp-cream h-9"
                        />
                        <button onClick={() => saveInternalNotes(o.orderNumber)} data-testid={`save-notes-${o.orderNumber}`} className="px-4 h-9 border border-alp-line text-alp-cream text-xs tracking-[0.15em] uppercase hover:border-alp-gold hover:text-alp-gold transition-colors">Save</button>
                      </div>
                    </div>
                    </div>
                    )}
                  </div>
                );})}
              </div>
              {filtered.length > ordersShown && (
                <button onClick={() => setOrdersShown((n) => n + 20)} data-testid="orders-load-more"
                  className="mt-4 w-full border border-alp-line text-alp-gray rounded py-3 text-xs tracking-[0.2em] uppercase hover:border-alp-gold hover:text-alp-gold transition-colors">
                  Show more ({filtered.length - ordersShown} older orders)
                </button>
              )}
              </>
            )})()}
          </TabsContent>

          {/* DISCOUNTS */}
          <TabsContent value="discounts" className="mt-0">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-serif text-2xl text-alp-cream">Discount Codes</h2>
              <button onClick={() => setEditingDiscount({ ...EMPTY_DISCOUNT })} data-testid="new-discount-btn" className="inline-flex items-center gap-2 bg-alp-gold text-alp-black px-5 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors"><Plus size={15} /> New Code</button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4" data-testid="discount-stats">
              {(() => {
                const paidSales = discountUsage.reduce((s, u) => s + (u.paidSales || 0), 0);
                const given = discountUsage.reduce((s, u) => s + (u.totalDiscount || 0), 0);
                const owed = discountUsage.filter((u) => u.affiliate).reduce((s, u) => s + (u.commissionOutstanding ?? u.commissionOwed ?? 0), 0);
                return [['Active codes', discounts.filter((d) => d.active).length, 'text-alp-cream'],
                  ['Paid sales via codes', `$${paidSales.toFixed(2)}`, 'text-alp-gold'],
                  ['Discounts given', `-$${given.toFixed(2)}`, 'text-amber-300'],
                  ['Affiliate owed', `$${owed.toFixed(2)}`, 'text-emerald-300']].map(([l, v, c]) => (
                    <div key={l} className="bg-alp-card/60 border border-alp-line rounded-lg p-3">
                      <div className="text-[10px] uppercase tracking-wider text-alp-gray">{l}</div>
                      <div className={`text-lg font-semibold ${c}`}>{v}</div>
                    </div>
                ));
              })()}
            </div>
            <div className="flex flex-wrap gap-2 mb-5">
              <div className="relative flex-1 min-w-[200px]">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-alp-gray" />
                <Input data-testid="discount-search" placeholder="Search codes, affiliates, descriptions…" value={discountSearch} onChange={(e) => setDiscountSearch(e.target.value)} className="bg-alp-dark border-alp-line text-alp-cream pl-9" />
              </div>
              {['all', 'active', 'inactive'].map((f) => (
                <button key={f} data-testid={`discount-filter-${f}`} onClick={() => setDiscountFilter(f)}
                  className={`px-3 py-1.5 rounded-full text-xs border capitalize transition-colors ${discountFilter === f ? 'bg-alp-gold text-alp-black border-alp-gold font-semibold' : 'bg-alp-card/60 text-alp-cream border-alp-line hover:border-alp-gold/60'}`}>{f}</button>
              ))}
            </div>
            {!discountsLoaded ? (
              <p className="text-alp-gray py-12 text-center"><Loader2 size={18} className="animate-spin inline mr-2" />Loading discount codes…</p>
            ) : discounts.length === 0 ? (
              <p className="text-alp-gray py-12 text-center">No discount codes yet. Create one to offer at checkout.</p>
            ) : (
              <div className="space-y-3">
                {(() => {
                  const filtered = discounts
                    .filter((d) => {
                      const q = discountSearch.trim().toLowerCase();
                      const hit = !q || d.code.toLowerCase().includes(q) || (d.affiliateName || '').toLowerCase().includes(q) || (d.description || '').toLowerCase().includes(q) || (d.restrictEmail || '').toLowerCase().includes(q);
                      const state = discountFilter === 'all' || (discountFilter === 'active') === !!d.active;
                      return hit && state;
                    });
                  if (filtered.length === 0) return <p className="text-alp-gray py-8 text-center border border-alp-line/60 rounded bg-alp-card/20" data-testid="discount-no-match">No codes match your search or filter.</p>;
                  return filtered.map((d) => (
                  <div key={d.id} data-testid={`discount-${d.code}`} className="border border-alp-line rounded p-4 bg-alp-card/40 flex flex-wrap items-center gap-4">
                    <div className="flex-1 min-w-[160px]">
                      <div className="font-serif text-xl text-alp-cream flex items-center gap-2">
                        {d.code}
                        {!d.active && <span className="text-[10px] tracking-[0.15em] uppercase text-red-400 border border-red-400/40 rounded px-1.5 py-0.5">Inactive</span>}
                        {d.vip && <span className="text-[10px] tracking-[0.15em] uppercase text-alp-gold border border-alp-gold/40 rounded px-1.5 py-0.5">VIP</span>}
                        {d.affiliate && <span className="text-[10px] tracking-[0.15em] uppercase text-emerald-400 border border-emerald-400/40 rounded px-1.5 py-0.5">Affiliate{d.affiliateName ? ` · ${d.affiliateName}` : ''}</span>}
                        {d.restrictEmail && <span className="text-[10px] tracking-[0.15em] uppercase text-sky-300 border border-sky-400/40 rounded px-1.5 py-0.5">Locked · {d.restrictEmail}</span>}
                      </div>
                      <div className="text-alp-gray text-sm">
                        {d.type === 'percentage' ? `${d.amount}% off` : d.type === 'freeItem' ? `Free gift: ${(products.find((p) => p.id === d.freeProductId) || {}).name || 'product'}` : `$${Number(d.amount).toFixed(2)} off`}
                        {d.minOrder ? ` \u00b7 min $${Number(d.minOrder).toFixed(2)}` : ''}
                        {d.expiry ? ` \u00b7 exp ${d.expiry}` : ''}
                        {` \u00b7 used ${d.usageCount || 0}${d.usageLimit ? '/' + d.usageLimit : ''}`}
                      </div>
                      {(() => {
                        const u = discountUsage.find((x) => x.code === d.code);
                        return u ? <div className="text-emerald-300 text-xs mt-0.5" data-testid={`discount-revenue-${d.code}`}>${(u.paidSales || 0).toFixed(2)} paid sales · {u.paidCount || 0} paid order{u.paidCount === 1 ? '' : 's'}</div> : null;
                      })()}
                    </div>
                    <button onClick={() => toggleDiscountActive(d)} data-testid={`toggle-discount-${d.code}`} className="text-xs tracking-[0.12em] uppercase border border-alp-line px-3 py-1.5 rounded text-alp-cream hover:border-alp-gold hover:text-alp-gold transition-colors">{d.active ? 'Disable' : 'Enable'}</button>
                    <button data-testid={`edit-discount-${d.code}`} onClick={() => setEditingDiscount({ ...d, expiry: d.expiry || '', usageLimit: d.usageLimit ?? '' })} className="p-2 text-alp-cream hover:text-alp-gold transition-colors"><Pencil size={17} /></button>
                    <button data-testid={`delete-discount-${d.code}`} onClick={() => deleteDiscount(d.id)} className="p-2 text-alp-gray hover:text-red-400 transition-colors"><Trash2 size={17} /></button>
                  </div>
                  ));
                })()}
              </div>
            )}

            {/* Code sales & affiliate rewards */}
            <div className="mt-12" data-testid="code-sales-section">
              <h2 className="font-serif text-2xl text-alp-cream mb-1">Code Sales &amp; Affiliate Rewards</h2>
              <p className="text-alp-gray text-sm mb-6">Track sales generated by each code so you can reward your affiliates. Cancelled orders are excluded.</p>
              {(() => {
                const affs = discountUsage.filter((u) => u.affiliate && u.commissionPercent > 0);
                const totalOwed = affs.reduce((s, u) => s + (u.commissionOwed || 0), 0);
                if (affs.length === 0) return null;
                return (
                  <div data-testid="affiliate-payout-banner" className="mb-6 border border-emerald-400/30 rounded-lg bg-emerald-400/5 p-5">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-emerald-300/80">Affiliate commission owed</div>
                    <div className="font-serif text-3xl text-emerald-300" data-testid="total-commission-owed">${totalOwed.toFixed(2)}</div>
                    <div className="text-alp-gray text-xs">across {affs.length} affiliate code{affs.length === 1 ? '' : 's'} &middot; based on paid sales only</div>
                    <div className="mt-4 divide-y divide-alp-line/40 border-t border-alp-line/40">
                      {affs.map((u) => (
                        <div key={`payout-${u.code}`} data-testid={`payout-${u.code}`} className="flex flex-wrap items-center justify-between py-2 text-sm gap-3">
                          <span className="text-alp-cream">{u.affiliateName || u.code} <span className="text-alp-gray text-xs">({u.code} &middot; {u.commissionPercent}%)</span></span>
                          <span className="flex items-center gap-3">
                            <span className="text-emerald-300 whitespace-nowrap">${(u.commissionOutstanding ?? u.commissionOwed).toFixed(2)} owed{u.commissionPaid > 0 ? <span className="text-alp-gray text-xs"> &middot; ${u.commissionPaid.toFixed(2)} paid</span> : null} <span className="text-alp-gray text-xs">on ${u.paidSales.toFixed(2)} paid sales</span></span>
                            <button onClick={() => payCommissionAsCredit(u)} data-testid={`pay-credit-${u.code}`} className="text-[10px] tracking-[0.1em] uppercase border border-emerald-400/50 text-emerald-300 rounded px-2.5 py-1 hover:bg-emerald-400/10 transition-colors">Pay as credit</button>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
              {discountUsage.length === 0 ? (
                <p className="text-alp-gray py-8 text-center border border-alp-line/60 rounded bg-alp-card/20">No orders have used a discount code yet.</p>
              ) : (
                <div className="space-y-3">
                  {discountUsage.map((u) => (
                    <div key={u.code} data-testid={`code-sales-${u.code}`} className="border border-alp-line rounded bg-alp-card/40">
                      <button onClick={() => setExpandedCode((c) => (c === u.code ? '' : u.code))} data-testid={`toggle-code-sales-${u.code}`} className="w-full text-left p-4 flex flex-wrap items-center gap-4 hover:bg-alp-card/60 transition-colors">
                        <div className="flex-1 min-w-[180px]">
                          <div className="font-serif text-xl text-alp-cream flex items-center gap-2 flex-wrap">
                            {u.code}
                            {u.affiliate && <span className="text-[10px] tracking-[0.15em] uppercase text-emerald-400 border border-emerald-400/40 rounded px-1.5 py-0.5">Affiliate{u.affiliateName ? ` \u00b7 ${u.affiliateName}` : ''}</span>}
                            {!u.exists && <span className="text-[10px] tracking-[0.15em] uppercase text-alp-gray border border-alp-line rounded px-1.5 py-0.5">Deleted code</span>}
                          </div>
                          <div className="text-alp-gray text-sm">{u.orderCount} order{u.orderCount === 1 ? '' : 's'} &middot; {u.paidCount} paid</div>
                        </div>
                        <div className="text-right">
                          <div className="text-alp-gold font-serif text-xl">${u.totalSales.toFixed(2)}</div>
                          <div className="text-alp-gray text-xs">sales &middot; &minus;${u.totalDiscount.toFixed(2)} given</div>
                          {u.affiliate && u.commissionPercent > 0 && <div className="text-emerald-300 text-xs" data-testid={`code-commission-${u.code}`}>owed ${u.commissionOwed.toFixed(2)} ({u.commissionPercent}%)</div>}
                        </div>
                        <ChevronLeft size={18} className={`text-alp-gray transition-transform ${expandedCode === u.code ? '-rotate-90' : 'rotate-180'}`} />
                      </button>
                      {expandedCode === u.code && (
                        <div className="border-t border-alp-line/60 p-4 overflow-x-auto" data-testid={`code-orders-${u.code}`}>
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-alp-gray text-[10px] tracking-[0.15em] uppercase">
                                <th className="text-left pb-2 pr-4">Order</th>
                                <th className="text-left pb-2 pr-4">Customer</th>
                                <th className="text-right pb-2 pr-4">Total</th>
                                <th className="text-right pb-2 pr-4">Discount</th>
                                <th className="text-left pb-2 pr-4">Payment</th>
                                <th className="text-left pb-2">Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              {u.orders.map((o) => (
                                <tr key={o.orderNumber} className="border-t border-alp-line/30">
                                  <td className="py-2 pr-4 text-alp-cream">{o.orderNumber}</td>
                                  <td className="py-2 pr-4 text-alp-cream">{o.customerName}<div className="text-alp-gray text-xs">{o.customerEmail}</div></td>
                                  <td className="py-2 pr-4 text-right text-alp-gold">${o.total.toFixed(2)}</td>
                                  <td className="py-2 pr-4 text-right text-alp-gray">&minus;${o.discountAmount.toFixed(2)}</td>
                                  <td className="py-2 pr-4"><span className={o.paymentStatus === 'Paid' ? 'text-emerald-400' : 'text-alp-gray'}>{o.paymentStatus}</span></td>
                                  <td className="py-2 text-alp-gray">{(o.createdAt || '').slice(0, 10)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* MARKETING */}
          <TabsContent value="blackfriday" className="mt-0">
            <BlackFridayPanel />
          </TabsContent>

          <TabsContent value="marketing" className="mt-0">            <TabInsights tab="marketing" />
            <div className="mb-8"><CampaignPanel /></div>

            {settingsDraft && (
              <div className="border border-alp-line rounded p-6 bg-alp-card/40 mb-8 max-w-3xl">
                <h3 className="font-serif text-xl text-alp-cream mb-1 flex items-center gap-2"><ImageIcon size={18} /> Homepage Hero Image</h3>
                <p className="text-alp-gray text-sm mb-5">Upload a new hero image for the homepage (JPG, PNG or WebP, max 8MB).</p>
                <div className="flex flex-wrap items-center gap-5">
                  <div className="w-56 aspect-[16/9] rounded overflow-hidden bg-alp-dark border border-alp-line flex items-center justify-center">
                    {settingsDraft.heroImage ? (
                      <img src={settingsDraft.heroImage} alt="Homepage hero" data-testid="hero-preview" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-alp-gray text-xs">Default image</span>
                    )}
                  </div>
                  <div>
                    <label className="inline-flex items-center gap-2 cursor-pointer bg-alp-gold text-alp-black px-5 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors">
                      <Upload size={14} /> {heroUploading ? 'Uploading\u2026' : 'Upload Image'}
                      <input type="file" accept="image/*" data-testid="hero-upload-input" className="hidden" disabled={heroUploading} onChange={(e) => uploadHero(e.target.files?.[0])} />
                    </label>
                    {settingsDraft.heroImage && (
                      <button onClick={async () => { const s = await adminUpdateSettings({ heroImage: '' }); setStoreSettings(s); setSettingsDraft(s); toast({ title: 'Reverted to default image' }); }} data-testid="hero-reset-btn" className="block mt-3 text-xs tracking-[0.12em] uppercase text-alp-gray hover:text-red-400 transition-colors">Reset to default</button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {settingsDraft && (
              <div className="border border-alp-line rounded p-6 bg-alp-card/40 mb-8 max-w-3xl">
                <h3 className="font-serif text-xl text-alp-cream mb-1">Homepage Announcement Banner</h3>
                <p className="text-alp-gray text-sm mb-5">A thin bar shown at the top of every page when active and within the date range.</p>
                <Box label="Banner Text"><Input value={settingsDraft.announcementText || ''} onChange={(e) => setf('announcementText', e.target.value)} placeholder="e.g. Free express shipping on orders over $300" data-testid="announcement-text" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <Box label="Button Text (optional)"><Input value={settingsDraft.announcementButtonText || ''} onChange={(e) => setf('announcementButtonText', e.target.value)} placeholder="Shop Now" data-testid="announcement-btn-text" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                  <Box label="Button Link (optional)"><Input value={settingsDraft.announcementButtonLink || ''} onChange={(e) => setf('announcementButtonLink', e.target.value)} placeholder="/shop" data-testid="announcement-btn-link" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                  <Box label="Start Date"><Input type="date" value={settingsDraft.announcementStart || ''} onChange={(e) => setf('announcementStart', e.target.value)} data-testid="announcement-start" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                  <Box label="End Date"><Input type="date" value={settingsDraft.announcementEnd || ''} onChange={(e) => setf('announcementEnd', e.target.value)} data-testid="announcement-end" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                </div>
                <label className="flex items-center gap-2 text-sm text-alp-cream/80 mt-4"><input type="checkbox" checked={!!settingsDraft.announcementActive} onChange={(e) => setf('announcementActive', e.target.checked)} data-testid="announcement-active" /> Active</label>
                <div className="mt-5 max-w-sm">
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs tracking-[0.15em] uppercase text-alp-gray">Scroll Speed</label>
                    <span className="text-alp-gold text-xs font-semibold" data-testid="announcement-speed-value">{settingsDraft.announcementSpeed || 2}x</span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    step="1"
                    value={settingsDraft.announcementSpeed || 2}
                    onChange={(e) => setf('announcementSpeed', Number(e.target.value))}
                    data-testid="announcement-speed"
                    className="w-full accent-alp-gold"
                  />
                  <div className="flex justify-between text-[10px] tracking-[0.15em] uppercase text-alp-gray mt-1"><span>Slow</span><span>Fast</span></div>
                </div>
                <div className="mt-5 flex flex-wrap gap-6" data-testid="announcement-colors">
                  <div>
                    <label className="text-xs tracking-[0.15em] uppercase text-alp-gray mb-1.5 block">Banner Colour</label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={settingsDraft.announcementBg || '#C9A227'} onChange={(e) => setf('announcementBg', e.target.value)} data-testid="announcement-bg-color" className="w-11 h-9 rounded border border-alp-line bg-transparent cursor-pointer p-0.5" />
                      <span className="text-alp-gray text-xs font-mono">{(settingsDraft.announcementBg || '#C9A227').toUpperCase()}</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs tracking-[0.15em] uppercase text-alp-gray mb-1.5 block">Text Colour</label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={settingsDraft.announcementColor || '#0A0A0A'} onChange={(e) => setf('announcementColor', e.target.value)} data-testid="announcement-text-color" className="w-11 h-9 rounded border border-alp-line bg-transparent cursor-pointer p-0.5" />
                      <span className="text-alp-gray text-xs font-mono">{(settingsDraft.announcementColor || '#0A0A0A').toUpperCase()}</span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-[160px]">
                    <label className="text-xs tracking-[0.15em] uppercase text-alp-gray mb-1.5 block">Preview</label>
                    <div className="h-9 rounded flex items-center justify-center text-xs font-medium px-3 overflow-hidden" style={{ backgroundColor: settingsDraft.announcementBg || '#C9A227', color: settingsDraft.announcementColor || '#0A0A0A' }} data-testid="announcement-preview">{settingsDraft.announcementText || 'Banner preview'}</div>
                  </div>
                </div>
                <button onClick={saveSettings} data-testid="save-announcement-btn" className="mt-5 bg-alp-gold text-alp-black px-6 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors">Save Banner</button>
              </div>
            )}

            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="font-serif text-xl text-alp-cream">Promotions</h3>
                <p className="text-alp-gray text-sm">Active promotions (within date range) appear on the homepage.</p>
              </div>
              <button onClick={() => setEditingPromo({ ...EMPTY_PROMO })} data-testid="new-promo-btn" className="inline-flex items-center gap-2 bg-alp-gold text-alp-black px-5 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors"><Plus size={15} /> New Promotion</button>
            </div>
            {promotions.length === 0 ? (
              <p className="text-alp-gray py-12 text-center">No promotions yet. Create one to feature on the homepage.</p>
            ) : (
              <div className="space-y-3">
                {promotions.map((p) => (
                  <div key={p.id} data-testid={`promo-${p.id}`} className="border border-alp-line rounded p-4 bg-alp-card/40 flex flex-wrap items-center gap-4">
                    {p.image ? <img src={p.image} alt={p.name} className="w-14 h-14 object-cover rounded bg-alp-dark" /> : null}
                    <div className="flex-1 min-w-[180px]">
                      <div className="font-serif text-xl text-alp-cream flex items-center gap-2">
                        {p.name}
                        {!p.active && <span className="text-[10px] tracking-[0.15em] uppercase text-red-400 border border-red-400/40 rounded px-1.5 py-0.5">Inactive</span>}
                      </div>
                      <div className="text-alp-gray text-sm">{p.type}{p.startDate ? ` \u00b7 ${p.startDate}` : ''}{p.endDate ? ` \u2192 ${p.endDate}` : ''}</div>
                    </div>
                    <button onClick={() => togglePromo(p)} data-testid={`toggle-promo-${p.id}`} className="text-xs tracking-[0.12em] uppercase border border-alp-line px-3 py-1.5 rounded text-alp-cream hover:border-alp-gold hover:text-alp-gold transition-colors">{p.active ? 'Disable' : 'Enable'}</button>
                    <button onClick={() => setEditingPromo({ ...p })} className="p-2 text-alp-cream hover:text-alp-gold transition-colors"><Pencil size={17} /></button>
                    <button onClick={() => deletePromo(p.id)} className="p-2 text-alp-gray hover:text-red-400 transition-colors"><Trash2 size={17} /></button>
                  </div>
                ))}
              </div>
            )}

            {/* UPCOMING / ARRIVING SOON */}
            <div className="flex justify-between items-center mb-6 mt-12">
              <div>
                <h3 className="font-serif text-xl text-alp-cream flex items-center gap-2"><Clock size={18} /> Upcoming / Arriving Soon</h3>
                <p className="text-alp-gray text-sm">Active items appear in the "Arriving Soon" section on the homepage and shop.</p>
              </div>
              <button onClick={() => setEditingIncoming({ ...EMPTY_INCOMING })} data-testid="new-incoming-btn" className="inline-flex items-center gap-2 bg-alp-gold text-alp-black px-5 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors"><Plus size={15} /> Add Upcoming</button>
            </div>
            {incoming.length === 0 ? (
              <p className="text-alp-gray py-8 text-center">No upcoming products yet.</p>
            ) : (
              <div className="space-y-3">
                {incoming.map((it) => (
                  <div key={it.id} data-testid={`incoming-${it.id}`} className="border border-alp-line rounded p-4 bg-alp-card/40 flex flex-wrap items-center gap-4">
                    {it.image ? <img src={it.image} alt={it.name} className="w-14 h-14 object-cover rounded bg-alp-dark" /> : null}
                    <div className="flex-1 min-w-[180px]">
                      <div className="font-serif text-xl text-alp-cream flex items-center gap-2">
                        {it.name}
                        {!it.active && <span className="text-[10px] tracking-[0.15em] uppercase text-red-400 border border-red-400/40 rounded px-1.5 py-0.5">Inactive</span>}
                      </div>
                      <div className="text-alp-gray text-sm">{it.eta ? `ETA: ${it.eta}` : 'No ETA set'}</div>
                    </div>
                    <button onClick={() => setEditingIncoming({ ...it })} className="p-2 text-alp-cream hover:text-alp-gold transition-colors"><Pencil size={17} /></button>
                    <button onClick={() => deleteIncoming(it.id)} className="p-2 text-alp-gray hover:text-red-400 transition-colors"><Trash2 size={17} /></button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* REVIEWS */}
          <TabsContent value="reviews" className="mt-0">
            <TabInsights tab="reviews" />
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-serif text-2xl text-alp-cream">Reviews</h2>
                <p className="text-alp-gray text-sm">Add, edit and manage customer reviews shown on the Reviews page and homepage.</p>
              </div>
              <button onClick={() => setEditingReview({ ...EMPTY_REVIEW })} data-testid="new-review-btn" className="inline-flex items-center gap-2 bg-alp-gold text-alp-black px-5 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors"><Plus size={15} /> Add Review</button>
            </div>
            {reviews.length === 0 ? (
              <p className="text-alp-gray text-sm py-10 text-center">No reviews yet. Add your first one.</p>
            ) : (
              <div className="space-y-3">
                {reviews.map((r) => (
                  <div key={r.id} data-testid={`admin-review-${r.id}`} className="flex items-start gap-4 border border-alp-line rounded p-4 bg-alp-card/40">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-alp-cream font-medium">{r.name}</span>
                        <span className="flex items-center gap-0.5">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star key={i} size={13} className={i < r.rating ? 'text-alp-gold fill-alp-gold' : 'text-alp-line'} />
                          ))}
                        </span>
                        {r.featured ? <span className="text-[9px] tracking-[0.12em] uppercase text-alp-gold border border-alp-gold/40 rounded-full px-2 py-0.5">Featured</span> : null}
                        {!r.active ? <span className="text-[9px] tracking-[0.12em] uppercase text-alp-gray border border-alp-line rounded-full px-2 py-0.5">Hidden</span> : null}
                      </div>
                      {r.body ? <p className="text-alp-cream/70 text-sm mt-1 line-clamp-2">{r.body}</p> : null}
                      <p className="text-alp-gray text-xs mt-1">{[r.productName, r.date].filter(Boolean).join(' \u00b7 ')}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => setEditingReview(r)} data-testid={`edit-review-${r.id}`} className="text-alp-gray hover:text-alp-gold p-1.5"><Pencil size={16} /></button>
                      <button onClick={() => deleteReview(r.id)} data-testid={`delete-review-${r.id}`} className="text-alp-gray hover:text-red-400 p-1.5"><Trash2 size={16} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>


          {/* SEO */}
          <TabsContent value="seo" className="mt-0">
            {settingsDraft && (
              <div className="max-w-2xl space-y-6" data-testid="seo-panel">
                <div className="border border-alp-line rounded p-6 bg-alp-card/40">
                  <h3 className="font-serif text-xl text-alp-cream mb-1">Search Engine (Google)</h3>
                  <p className="text-alp-gray text-sm mb-5">Controls how your site appears in Google search results. Applied across the site.</p>
                  <Box label="Page Title"><Input value={settingsDraft.seoTitle || ''} onChange={(e) => setf('seoTitle', e.target.value)} data-testid="seo-title" placeholder="Australian Luxe Peptides | Science. Purity. Elevated." className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                  <Box label="Meta Description (~155 chars)"><Textarea value={settingsDraft.seoDescription || ''} onChange={(e) => setf('seoDescription', e.target.value)} rows={2} data-testid="seo-description" className="bg-alp-dark border-alp-line text-alp-cream mt-4" /></Box>
                  <p className="text-xs text-alp-gray/60 mt-1">{(settingsDraft.seoDescription || '').length} characters</p>
                  <Box label="Keywords (comma separated)"><Textarea value={settingsDraft.seoKeywords || ''} onChange={(e) => setf('seoKeywords', e.target.value)} rows={2} data-testid="seo-keywords" className="bg-alp-dark border-alp-line text-alp-cream mt-4" /></Box>
                </div>

                <div className="border border-alp-line rounded p-6 bg-alp-card/40">
                  <h3 className="font-serif text-xl text-alp-cream mb-1">Social Sharing (Open Graph)</h3>
                  <p className="text-alp-gray text-sm mb-5">How your links look when shared on Facebook, WhatsApp, iMessage etc. Leave title/description blank to reuse the Google ones.</p>
                  <Box label="Social Title (optional)"><Input value={settingsDraft.ogTitle || ''} onChange={(e) => setf('ogTitle', e.target.value)} data-testid="seo-og-title" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                  <Box label="Social Description (optional)"><Textarea value={settingsDraft.ogDescription || ''} onChange={(e) => setf('ogDescription', e.target.value)} rows={2} data-testid="seo-og-description" className="bg-alp-dark border-alp-line text-alp-cream mt-4" /></Box>
                  <div className="mt-4">
                    <Label className="text-xs tracking-[0.15em] uppercase text-alp-gray mb-2 block">Share Image (1200x630 recommended)</Label>
                    <div className="flex items-center gap-4">
                      {settingsDraft.ogImage ? (
                        <img src={settingsDraft.ogImage} alt="share preview" data-testid="seo-og-preview" className="w-28 h-16 object-cover rounded bg-alp-dark border border-alp-line" />
                      ) : (
                        <div className="w-28 h-16 rounded bg-alp-dark border border-alp-line flex items-center justify-center text-alp-gray text-[10px]">No image</div>
                      )}
                      <label className="inline-flex items-center gap-2 cursor-pointer text-xs tracking-[0.15em] uppercase border border-alp-line px-4 py-2 rounded text-alp-cream hover:border-alp-gold hover:text-alp-gold transition-colors">
                        <Upload size={14} /> {heroUploading ? 'Uploading\u2026' : 'Upload'}
                        <input type="file" accept="image/*" className="hidden" data-testid="seo-og-upload" disabled={heroUploading} onChange={(e) => uploadOgImage(e.target.files?.[0])} />
                      </label>
                      {settingsDraft.ogImage ? <button type="button" onClick={() => setf('ogImage', '')} className="text-xs tracking-[0.12em] uppercase text-alp-gray hover:text-red-400 transition-colors">Clear</button> : null}
                    </div>
                    <Box label="Or paste image URL"><Input value={settingsDraft.ogImage || ''} onChange={(e) => setf('ogImage', e.target.value)} placeholder="https://..." data-testid="seo-og-image-url" className="bg-alp-dark border-alp-line text-alp-cream mt-3" /></Box>
                  </div>
                </div>

                <div className="border border-alp-line rounded p-6 bg-alp-card/40">
                  <h3 className="font-serif text-xl text-alp-cream mb-1">Indexing</h3>
                  <p className="text-alp-gray text-sm mb-5">Canonical domain and whether search engines may index your site.</p>
                  <Box label="Canonical URL"><Input value={settingsDraft.canonicalUrl || ''} onChange={(e) => setf('canonicalUrl', e.target.value)} data-testid="seo-canonical" placeholder="https://australianluxe.org" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                  <label className="flex items-center gap-2 text-sm text-alp-cream/80 mt-4"><input type="checkbox" checked={settingsDraft.robotsIndex !== false} onChange={(e) => setf('robotsIndex', e.target.checked)} data-testid="seo-robots-index" /> Allow search engines to index this site</label>
                  <p className="text-xs text-alp-gray/60 mt-3">Sitemap auto-generates from your live products at <span className="text-alp-gold">{(settingsDraft.canonicalUrl || 'https://australianluxe.org')}/api/sitemap.xml</span></p>
                </div>

                <button onClick={saveSettings} data-testid="save-seo-btn" className="bg-alp-gold text-alp-black px-8 py-3 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors">Save SEO Settings</button>
              </div>
            )}
          </TabsContent>


          {/* CLIENT LIBRARY */}
          <TabsContent value="concierge" className="mt-0">
            <ConciergeLogPanel />
          </TabsContent>

          <TabsContent value="library" className="mt-0">
            <div className="border border-alp-line rounded p-6 bg-alp-card/40 mb-8 max-w-2xl">
              <h3 className="font-serif text-xl text-alp-cream mb-1 flex items-center gap-2"><Lock size={18} /> Library Access Password</h3>
              <p className="text-alp-gray text-sm mb-4">
                Clients enter this at <span className="text-alp-cream">/library</span> to view guides (view-only).
                {' '}Status: {libStatus?.passwordSet ? <span className="text-emerald-300/90" data-testid="lib-password-status">Password is set</span> : <span className="text-alp-gold" data-testid="lib-password-status">No password set yet</span>}
                {libStatus?.password ? <span className="ml-2">&middot; Current: <span className="text-alp-gold font-mono" data-testid="lib-current-password">{libStatus.password}</span></span> : null}
              </p>
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[220px]">
                  <Label className="text-[10px] tracking-[0.15em] uppercase text-alp-gray mb-1 block">{libStatus?.passwordSet ? 'New password' : 'Set password'}</Label>
                  <Input type="text" value={newLibPassword} onChange={(e) => setNewLibPassword(e.target.value)} placeholder="Enter a password to share with clients" data-testid="lib-password-input" className="bg-alp-dark border-alp-line text-alp-cream" />
                </div>
                <button onClick={saveLibPassword} data-testid="save-lib-password-btn" className="h-10 px-6 bg-alp-gold text-alp-black text-xs tracking-[0.15em] uppercase font-semibold hover:bg-alp-goldlight transition-colors">{libStatus?.passwordSet ? 'Update' : 'Set Password'}</button>
              </div>
            </div>

            <div className="border border-alp-line rounded p-6 bg-alp-card/40 mb-8 max-w-3xl" data-testid="access-log">
              <h3 className="font-serif text-xl text-alp-cream mb-1">Access Log</h3>
              <p className="text-alp-gray text-sm mb-4">Clients who can access the library (auto-added when a Library Access order is marked Payment Confirmed). Disable to instantly block someone.</p>
              <div className="flex flex-wrap items-end gap-3 mb-5">
                <div className="flex-1 min-w-[220px]">
                  <Label className="text-[10px] tracking-[0.15em] uppercase text-alp-gray mb-1 block">Grant access to an email</Label>
                  <Input type="email" value={newAccessEmail} onChange={(e) => setNewAccessEmail(e.target.value)} placeholder="client@example.com" data-testid="add-access-input" className="bg-alp-dark border-alp-line text-alp-cream" />
                </div>
                <button onClick={addAccess} data-testid="add-access-btn" className="h-10 px-6 bg-alp-gold text-alp-black text-xs tracking-[0.15em] uppercase font-semibold hover:bg-alp-goldlight transition-colors">Add</button>
              </div>
              {libAccess.length === 0 ? (
                <p className="text-alp-gray text-sm py-4 text-center">No clients have access yet.</p>
              ) : (
                <div className="space-y-2">
                  {libAccess.map((g) => (
                    <div key={g.id} data-testid={`access-${g.id}`} className="flex flex-wrap items-center gap-3 border border-alp-line/60 rounded px-3 py-2.5 text-sm">
                      <span className="text-alp-cream flex-1 min-w-[160px]">{g.email}{g.name ? <span className="text-alp-gray"> &middot; {g.name}</span> : null}</span>
                      <span className="text-alp-gray text-xs">granted {(g.grantedAt || '').slice(0, 10)}</span>
                      <span className="text-alp-gray text-xs">{g.lastAccessAt ? `last ${g.lastAccessAt.slice(0, 10)}` : 'never used'}</span>
                      <span className={`text-[10px] tracking-[0.12em] uppercase rounded px-2 py-0.5 border ${g.status === 'active' ? 'text-emerald-300/90 border-emerald-400/40' : 'text-red-400 border-red-400/40'}`}>{g.status}</span>
                      <button onClick={() => toggleAccess(g)} data-testid={`toggle-access-${g.id}`} className="text-xs tracking-[0.1em] uppercase border border-alp-line px-3 py-1.5 rounded text-alp-cream hover:border-alp-gold hover:text-alp-gold transition-colors">{g.status === 'active' ? 'Disable' : 'Enable'}</button>
                      <button onClick={() => removeAccess(g.id)} className="p-1.5 text-alp-gray hover:text-red-400 transition-colors"><Trash2 size={15} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="font-serif text-xl text-alp-cream">Help Guides</h3>
                <p className="text-alp-gray text-sm">Add how-to guides with title, written content and an image. Grouped by category.</p>
              </div>
              <button onClick={() => setEditingLibItem({ ...EMPTY_LIB_ITEM })} data-testid="new-lib-item-btn" className="inline-flex items-center gap-2 bg-alp-gold text-alp-black px-5 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors"><Plus size={15} /> New Guide</button>
            </div>
            {libItems.length === 0 ? (
              <p className="text-alp-gray py-10 text-center">No guides yet.</p>
            ) : (
              <div className="space-y-3">
                {libItems.map((it) => (
                  <div key={it.id} data-testid={`lib-item-${it.id}`} className="border border-alp-line rounded p-4 bg-alp-card/40 flex flex-wrap items-center gap-4">
                    {it.image ? <img src={it.image} alt={it.title} className="w-14 h-14 object-cover rounded bg-alp-dark" /> : null}
                    <div className="flex-1 min-w-[180px]">
                      <div className="font-serif text-xl text-alp-cream flex items-center gap-2">
                        {it.title}
                        {!it.active && <span className="text-[10px] tracking-[0.15em] uppercase text-red-400 border border-red-400/40 rounded px-1.5 py-0.5">Hidden</span>}
                      </div>
                      <div className="text-alp-gray text-sm">{it.category} &middot; order {it.order}</div>
                    </div>
                    <button onClick={() => setEditingLibItem({ ...it })} className="p-2 text-alp-cream hover:text-alp-gold transition-colors"><Pencil size={17} /></button>
                    <button onClick={() => deleteLibItem(it.id)} className="p-2 text-alp-gray hover:text-red-400 transition-colors"><Trash2 size={17} /></button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* PROFIT / LOSS */}
          <TabsContent value="assistant" className="mt-0">
            <AdminAssistant />
          </TabsContent>

          <TabsContent value="findash" className="mt-0">
            <FinancialDashboard />
          </TabsContent>

          <TabsContent value="money" className="mt-0">
            <MonthlyMoney />
          </TabsContent>

          <TabsContent value="crm" className="mt-0">
            <CrmPanel />
          </TabsContent>

          <TabsContent value="crew" className="mt-0">
            <CrewAdminPanel />
          </TabsContent>

          <TabsContent value="abandoned" className="mt-0">
            <AbandonedPanel />
          </TabsContent>

          <TabsContent value="winback" className="mt-0">
            <WinBackPanel />
          </TabsContent>

          <TabsContent value="birthdays" className="mt-0">
            <BirthdayPanel />
          </TabsContent>

          <TabsContent value="giftcards" className="mt-0">
            <GiftCardsPanel />
          </TabsContent>

          <TabsContent value="forecast" className="mt-0">
            <TabInsights tab="forecast" />
            <ForecastPanel />
          </TabsContent>

          <TabsContent value="pnl" className="mt-0">
            <TabInsights tab="pnl" />
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <div>
                <h2 className="font-serif text-2xl text-alp-cream">Profit &amp; Loss</h2>
                <p className="text-alp-gray text-sm">Based on all orders except Cancelled. Sales = item price &times; qty. COGS = supplier cost + extra cost.</p>
              </div>
              <button onClick={loadPnl} disabled={pnlLoading} data-testid="refresh-pnl-btn" className="inline-flex items-center gap-2 text-xs tracking-[0.12em] uppercase border border-alp-line px-3 py-1.5 rounded text-alp-cream hover:border-alp-gold hover:text-alp-gold transition-colors disabled:opacity-50"><RefreshCw size={13} className={pnlLoading ? 'animate-spin' : ''} /> Refresh</button>
            </div>
            <div className="flex flex-wrap items-end gap-3 mb-6 border border-alp-line rounded p-4 bg-alp-card/40" data-testid="pnl-date-filter">
              <div>
                <label className="text-[10px] tracking-[0.15em] uppercase text-alp-gray mb-1.5 block">From</label>
                <input type="date" value={pnlStart} onChange={(e) => setPnlStart(e.target.value)} data-testid="pnl-start-date" className="h-9 rounded-md bg-alp-dark border border-alp-line text-alp-cream text-sm px-3" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.15em] uppercase text-alp-gray mb-1.5 block">To</label>
                <input type="date" value={pnlEnd} onChange={(e) => setPnlEnd(e.target.value)} data-testid="pnl-end-date" className="h-9 rounded-md bg-alp-dark border border-alp-line text-alp-cream text-sm px-3" />
              </div>
              <button onClick={loadPnl} disabled={pnlLoading} data-testid="pnl-apply-dates" className="h-9 bg-alp-gold text-alp-black px-5 text-xs tracking-[0.15em] uppercase font-semibold hover:bg-alp-goldlight transition-colors disabled:opacity-50 rounded-md">Apply</button>
              {(pnlStart || pnlEnd) && (
                <button onClick={clearPnlDates} disabled={pnlLoading} data-testid="pnl-clear-dates" className="h-9 border border-alp-line px-4 text-xs tracking-[0.15em] uppercase text-alp-gray hover:text-alp-gold hover:border-alp-gold transition-colors rounded-md">All time</button>
              )}
              <p className="text-alp-gray text-xs ml-auto self-center">{pnlStart || pnlEnd ? `Showing ${pnlStart || 'start'} → ${pnlEnd || 'today'}` : 'Showing all time'}</p>
            </div>
            {!pnl ? (
              <p className="text-alp-gray py-12 text-center">Loading profit &amp; loss&hellip;</p>
            ) : (
              <div className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="pnl-summary">
                  <Stat label="Total Sales" value={`$${pnl.totalSales.toFixed(2)}`} />
                  <Stat label="Total COGS" value={`$${pnl.totalCogs.toFixed(2)}`} />
                  <Stat label="Shipping Collected" value={`$${(pnl.totalShipping || 0).toFixed(2)}`} />
                  <Stat label="Discounts Given" value={`-$${(pnl.totalDiscounts || 0).toFixed(2)}`} />
                  <Stat label="Gross Profit" value={`$${pnl.grossProfit.toFixed(2)}`} gold />
                  <Stat label="Net Profit" value={`$${(pnl.netProfit ?? pnl.grossProfit).toFixed(2)}`} gold />
                  <Stat label="Avg Margin" value={`${pnl.avgMargin.toFixed(1)}%`} gold />
                  <Stat label="Orders" value={`${pnl.orderCount ?? 0}`} />
                </div>
                {pnl.invoiceIncome && pnl.invoiceIncome.count > 0 && (
                  <div className="border border-alp-line rounded-lg bg-alp-card/40 p-4" data-testid="pnl-invoice-income">
                    <div className="flex flex-wrap items-center justify-between mb-2">
                      <span className="text-[10px] tracking-[0.2em] uppercase text-alp-gold">Invoice income by cost centre</span>
                      <span className="text-alp-cream text-sm font-semibold">${pnl.invoiceIncome.total.toFixed(2)} <span className="text-alp-gray text-xs">· {pnl.invoiceIncome.count} paid invoice{pnl.invoiceIncome.count === 1 ? '' : 's'}</span></span>
                    </div>
                    {pnl.invoiceIncome.byCostCentre.map((c) => (
                      <div key={c.costCentre} className="flex justify-between text-sm py-1.5 border-t border-alp-line/40">
                        <span className="text-alp-cream">{c.costCentre} <span className="text-alp-gray text-xs">({c.count})</span></span>
                        <span className="text-alp-gold">${c.total.toFixed(2)} <span className="text-alp-gray text-xs">GST ${c.gst.toFixed(2)}</span></span>
                      </div>
                    ))}
                  </div>
                )}
                {pnl.spendingByCostCentre && pnl.spendingByCostCentre.count > 0 && (
                  <div className="border border-alp-line rounded-lg bg-alp-card/40 p-4" data-testid="pnl-spend-cost-centres">
                    <div className="flex flex-wrap items-center justify-between mb-2">
                      <span className="text-[10px] tracking-[0.2em] uppercase text-alp-gold">Spending by cost centre</span>
                      <span className="text-alp-cream text-sm font-semibold">${pnl.spendingByCostCentre.total.toFixed(2)} <span className="text-alp-gray text-xs">· {pnl.spendingByCostCentre.count} spending{pnl.spendingByCostCentre.count === 1 ? '' : 's'}</span></span>
                    </div>
                    {pnl.spendingByCostCentre.byCostCentre.map((c) => (
                      <div key={c.costCentre} className="flex justify-between text-sm py-1.5 border-t border-alp-line/40">
                        <span className="text-alp-cream">{c.costCentre} <span className="text-alp-gray text-xs">({c.count})</span></span>
                        <span className="text-alp-gold">${c.total.toFixed(2)} <span className="text-alp-gray text-xs">GST ${c.gst.toFixed(2)}</span></span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="border border-alp-line rounded overflow-hidden">
                  <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-alp-card/60 text-[10px] tracking-[0.15em] uppercase text-alp-gray">
                    <div className="col-span-4">Product</div>
                    <div className="col-span-1 text-right">Units</div>
                    <div className="col-span-2 text-right">Revenue</div>
                    <div className="col-span-2 text-right">COGS</div>
                    <div className="col-span-2 text-right">Gross Profit</div>
                    <div className="col-span-1 text-right">Margin</div>
                  </div>
                  {pnl.products.length === 0 ? (
                    <p className="text-alp-gray py-10 text-center text-sm">No sales yet.</p>
                  ) : pnl.products.map((p) => (
                    <div key={p.id || p.name} data-testid={`pnl-row-${p.id || p.name}`} onClick={() => setPnlOrdersFor(p)} className="grid grid-cols-12 gap-2 px-4 py-3 border-t border-alp-line/50 text-sm items-center cursor-pointer hover:bg-alp-gold/5 transition-colors">
                      <div className="col-span-4 text-alp-cream">{p.name} <span className="text-alp-gray text-xs">{p.size}</span></div>
                      <div className="col-span-1 text-right text-alp-cream">{p.unitsSold}</div>
                      <div className="col-span-2 text-right text-alp-cream">${p.revenue.toFixed(2)}</div>
                      <div className="col-span-2 text-right text-alp-gray">${p.cogs.toFixed(2)}</div>
                      <div className="col-span-2 text-right text-alp-gold">${p.grossProfit.toFixed(2)}</div>
                      <div className="col-span-1 text-right text-alp-cream">{p.margin.toFixed(1)}%</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* PRODUCTS */}
          <TabsContent value="products" className="mt-0">
            <TabInsights tab="products" />
            <div className="flex justify-between items-center mb-5 flex-wrap gap-3">
              <p className="text-alp-gray text-sm">Add, edit and delete products. Update prices, stock status and images.</p>
              <div className="flex items-center gap-2 flex-wrap">
                {products.some((p) => p.stockStatus === 'Out of Stock') && (
                  <button onClick={convertSoldout} data-testid="soldout-to-preorder-btn" className="inline-flex items-center gap-2 border border-alp-gold/50 text-alp-gold px-4 py-2.5 text-xs tracking-[0.15em] uppercase rounded hover:bg-alp-gold hover:text-alp-black transition-colors"><RefreshCw size={14} /> Sold Out → Preorder</button>
                )}
                <button onClick={() => setEditing({ ...EMPTY_PRODUCT })} className="inline-flex items-center gap-2 bg-alp-gold text-alp-black px-5 py-2.5 text-xs tracking-[0.15em] uppercase font-semibold hover:bg-alp-goldlight transition-colors"><Plus size={15} /> New Product</button>
              </div>
            </div>
            <div className="space-y-3">
              {products.map((p) => {
                const inv = p.inventory ?? 0;
                const low = !p.hidden && p.stockStatus !== 'Preorder' && inv <= 5;
                return (
                <div key={p.id} data-testid={`admin-product-${p.id}`} className={`border rounded p-4 flex flex-wrap items-center gap-4 ${p.hidden ? 'border-alp-line/50 bg-alp-card/20 opacity-60' : low ? 'border-amber-500/50 bg-amber-500/[0.04]' : 'border-alp-line bg-alp-card/40'}`}>
                  <img src={p.image} alt={p.name} className="w-14 h-14 object-cover rounded bg-alp-dark" />
                  <div className="flex-1 min-w-[160px]">
                    <div className="font-serif text-xl text-alp-cream flex items-center gap-2">
                      {p.name}
                      {p.hidden && <span className="text-[10px] tracking-[0.15em] uppercase text-red-400 border border-red-400/40 rounded px-1.5 py-0.5">Hidden</span>}
                      {low && <span className="text-[10px] tracking-[0.15em] uppercase text-amber-300 border border-amber-500/50 bg-amber-500/10 rounded px-1.5 py-0.5">{inv === 0 ? 'No stock' : 'Low stock'}</span>}
                    </div>
                    <div className="text-alp-gray text-sm">{p.code} &middot; {p.size} &middot; {p.category} &middot; {p.stockStatus}</div>
                  </div>
                  <div className="flex items-center gap-1" title="Stock on hand">
                    <button onClick={() => adjustStock(p, inv - 1)} data-testid={`stock-minus-${p.id}`} className="w-7 h-8 border border-alp-line rounded-l text-alp-cream hover:border-alp-gold hover:text-alp-gold transition-colors disabled:opacity-30" disabled={inv <= 0}>&minus;</button>
                    <input
                      type="number" min="0" value={inv}
                      onChange={(e) => adjustStock(p, e.target.value)}
                      data-testid={`stock-input-${p.id}`}
                      className={`w-14 h-8 bg-alp-dark border text-center text-sm outline-none focus:border-alp-gold ${low ? 'border-amber-500/50 text-amber-300' : 'border-alp-line text-alp-cream'}`}
                    />
                    <button onClick={() => adjustStock(p, inv + 1)} data-testid={`stock-plus-${p.id}`} className="w-7 h-8 border border-alp-line rounded-r text-alp-cream hover:border-alp-gold hover:text-alp-gold transition-colors">+</button>
                  </div>
                  <div className="text-alp-gold">${Number(p.price).toFixed(2)}</div>
                  <button onClick={() => toggleVisibility(p)} data-testid={`toggle-visibility-${p.id}`} title={p.hidden ? 'Show on shop' : 'Hide from shop'} className="p-2 text-alp-cream hover:text-alp-gold transition-colors">
                    {p.hidden ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                  <button onClick={() => setEditing(p)} data-testid={`edit-product-${p.id}`} className="p-2 text-alp-cream hover:text-alp-gold transition-colors" title="Edit"><Pencil size={17} /></button>
                  <button onClick={() => duplicateProduct(p)} data-testid={`duplicate-product-${p.id}`} className="p-2 text-alp-cream hover:text-alp-gold transition-colors" title="Duplicate"><Copy size={17} /></button>
                  <button onClick={() => deleteProduct(p.id)} data-testid={`delete-product-${p.id}`} className="p-2 text-alp-gray hover:text-red-400 transition-colors" title="Delete"><Trash2 size={17} /></button>
                </div>
              );})}
            </div>
          </TabsContent>

          {/* CONTACTS */}
          <TabsContent value="contacts" className="mt-0">
            {contacts.length === 0 ? (
              <p className="text-alp-gray py-16 text-center">No contact submissions yet.</p>
            ) : (
              <div className="space-y-3">
                {contacts.map((c) => (
                  <div key={c.id} data-testid={`contact-${c.id}`} className="border border-alp-line rounded p-5 bg-alp-card/40">
                    <div className="flex justify-between items-start gap-3">
                      <div className="text-alp-cream">{c.name} <span className="text-alp-gray">&middot; {c.email}</span></div>
                      <div className="flex items-center gap-3 shrink-0">
                        <div className="text-alp-gray text-xs">{new Date(c.createdAt).toLocaleString()}</div>
                        <button onClick={() => openReply(c)} data-testid={`reply-contact-${c.id}`} className="inline-flex items-center gap-1.5 text-xs tracking-[0.12em] uppercase border border-alp-gold/50 text-alp-gold px-3 py-1.5 rounded hover:bg-alp-gold hover:text-alp-black transition-colors">
                          <Reply size={13} /> Reply
                        </button>
                      </div>
                    </div>
                    <p className="text-alp-gray text-sm mt-2 whitespace-pre-wrap">{c.message}</p>
                    {(c.replies?.length > 0) && (
                      <div className="mt-4 space-y-2 border-t border-alp-line/60 pt-3" data-testid={`contact-replies-${c.id}`}>
                        <p className="text-[10px] tracking-[0.2em] uppercase text-alp-gold">Reply history</p>
                        {c.replies.map((r) => (
                          <div key={r.id} className="text-xs text-alp-cream/80 bg-alp-dark/60 rounded p-3">
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                              <span className="text-alp-cream">{r.subject}</span>
                              <span className="text-alp-gray inline-flex items-center gap-1.5">
                                {r.status === 'sent' ? <CheckCircle2 size={12} className="text-emerald-400" /> : <Clock size={12} className="text-alp-gold" />}
                                {r.status === 'sent' ? 'Sent' : 'Logged'} &middot; {new Date(r.createdAt).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-alp-gray mt-1.5 whitespace-pre-wrap">{r.message}</p>
                            {r.attachments?.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-2">
                                {r.attachments.map((a, i) => (
                                  <span key={i} className="inline-flex items-center gap-1 text-[11px] text-alp-gold/90 border border-alp-gold/30 rounded px-2 py-0.5"><Paperclip size={11} /> {a}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* EMAILS */}
          <TabsContent value="emails" className="mt-0">
            <div className="border border-alp-line rounded p-6 bg-alp-card/40 mb-6" data-testid="email-settings-card">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h3 className="font-serif text-xl text-alp-cream">Email Settings (SMTP)</h3>
                <button onClick={loadEmailStatus} disabled={emailStatusLoading} data-testid="recheck-smtp-btn" className="inline-flex items-center gap-2 text-xs tracking-[0.12em] uppercase border border-alp-line px-3 py-1.5 rounded text-alp-cream hover:border-alp-gold hover:text-alp-gold transition-colors disabled:opacity-50">
                  <RefreshCw size={13} className={emailStatusLoading ? 'animate-spin' : ''} /> Recheck
                </button>
              </div>
              {!emailStatus ? (
                <p className="text-alp-gray text-sm">Checking SMTP connection&hellip;</p>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-2" data-testid="smtp-status">
                    {emailStatus.connected ? (
                      <span className="inline-flex items-center gap-2 text-emerald-300/90 text-sm"><CheckCircle2 size={18} /> SMTP Connected</span>
                    ) : emailStatus.configured ? (
                      <span className="inline-flex items-center gap-2 text-red-400 text-sm"><XCircle size={18} /> Connection failed</span>
                    ) : (
                      <span className="inline-flex items-center gap-2 text-alp-gold text-sm"><XCircle size={18} /> Not configured (emails are logged only)</span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 text-sm">
                    <div className="text-alp-gray">Host: <span className="text-alp-cream">{emailStatus.host || '\u2014'}</span></div>
                    <div className="text-alp-gray">Port: <span className="text-alp-cream">{emailStatus.port || '\u2014'}</span></div>
                    <div className="text-alp-gray">Security: <span className="text-alp-cream">{emailStatus.secure || '\u2014'}</span></div>
                    <div className="text-alp-gray">From: <span className="text-alp-cream">{emailStatus.fromName} &lt;{emailStatus.fromEmail || '\u2014'}&gt;</span></div>
                    <div className="text-alp-gray">User: <span className="text-alp-cream">{emailStatus.user || '\u2014'}</span></div>
                    <div className="text-alp-gray">Password: <span className="text-alp-cream">{emailStatus.passwordSet ? '\u2022\u2022\u2022\u2022\u2022\u2022 (set)' : 'not set'}</span></div>
                    <div className="text-alp-gray">Admin alerts to: <span className="text-alp-cream">{emailStatus.adminOrderEmail || '\u2014'}</span></div>
                  </div>
                  {emailStatus.info ? <div className="text-alp-gray text-xs">{emailStatus.info}</div> : null}
                  {emailStatus.lastEmail ? (
                    <div className="text-xs text-alp-gray border-t border-alp-line/60 pt-3" data-testid="last-email-status">
                      Last email: <span className="text-alp-cream">{emailStatus.lastEmail.subject}</span> &rarr; {emailStatus.lastEmail.to}
                      {' '}<span className={emailStatus.lastEmail.status === 'sent' ? 'text-emerald-300/90' : emailStatus.lastEmail.status === 'failed' ? 'text-red-400' : 'text-alp-gold'}>[{emailStatus.lastEmail.status}]</span>
                    </div>
                  ) : null}
                  <div className="flex flex-wrap items-end gap-2 border-t border-alp-line/60 pt-4">
                    <div className="flex-1 min-w-[220px]">
                      <Label className="text-[10px] tracking-[0.15em] uppercase text-alp-gray mb-1 block">Send test email to</Label>
                      <Input value={testEmailTo} onChange={(e) => setTestEmailTo(e.target.value)} placeholder="you@example.com" data-testid="test-email-input" className="bg-alp-dark border-alp-line text-alp-cream h-9" />
                    </div>
                    <button onClick={sendTest} disabled={testSending} data-testid="send-test-email-btn" className="inline-flex items-center gap-2 h-9 px-5 bg-alp-gold text-alp-black text-xs tracking-[0.15em] uppercase font-semibold hover:bg-alp-goldlight transition-colors disabled:opacity-50">
                      <Send size={14} /> {testSending ? 'Sending\u2026' : 'Send Test'}
                    </button>
                  </div>
                </div>
              )}
            </div>
            <p className="text-alp-gray text-sm mb-4">Email log. When SMTP is connected emails are sent; otherwise they are stored here.</p>
            <div className="relative mb-5">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-alp-gray" />
              <input value={emailSearch} onChange={(e) => setEmailSearch(e.target.value)} data-testid="email-search-input"
                placeholder="Search by recipient, subject, type or status…"
                className="w-full bg-alp-dark border border-alp-line rounded pl-9 pr-3 py-2.5 text-sm text-alp-cream focus:border-alp-gold outline-none" />
            </div>
            {(() => {
              const q = emailSearch.trim().toLowerCase();
              const filtered = q
                ? emails.filter((e) => [e.to, e.subject, e.type, e.status].some((f) => (f || '').toLowerCase().includes(q)))
                : emails;
              return (
                <>
                  <p className="text-alp-gray text-xs mb-3" data-testid="email-count">{filtered.length} email{filtered.length === 1 ? '' : 's'}{q ? ` matching “${emailSearch.trim()}”` : ' (most recent 500)'}</p>
                  {filtered.length === 0 ? (
                    <p className="text-alp-gray py-16 text-center">{q ? 'No emails match that search.' : 'No emails yet.'}</p>
                  ) : (
                    <div className="space-y-3">
                      {filtered.map((e) => (
                        <div key={e.id} className="border border-alp-line rounded p-5 bg-alp-card/40" data-testid={`email-log-${e.id}`}>
                          <div className="flex flex-wrap justify-between gap-2">
                            <div className="text-alp-cream text-sm">{e.subject} <span className="text-alp-gray">&rarr; {e.to}</span></div>
                            <div className="flex items-center gap-2">
                              <span className={`text-[10px] tracking-[0.15em] uppercase px-2.5 py-1 rounded-full border ${e.status === 'sent' ? 'text-emerald-300/90 border-emerald-300/30' : e.status === 'failed' ? 'text-red-400 border-red-400/40' : 'text-alp-gold border-alp-gold/40'}`}>{e.status}</span>
                              <button data-testid={`email-resend-btn-${e.id}`}
                                onClick={async () => {
                                  try {
                                    const { data } = await http.post(`/admin/email-logs/${e.id}/resend`, {}, authCfg());
                                    toast({ title: data.status === 'sent' ? `Re-sent to ${data.to}` : `Resend ${data.status}` });
                                    adminGetEmailLogs().then(setEmails).catch(() => {});
                                  } catch (err) { toast({ title: err?.response?.data?.detail || 'Resend failed', variant: 'destructive' }); }
                                }}
                                className="text-[10px] tracking-[0.12em] uppercase border border-alp-gold/40 px-2.5 py-1 rounded text-alp-gold hover:bg-alp-gold/10 transition-colors">
                                Resend
                              </button>
                              <button data-testid={`email-preview-btn-${e.id}`}
                                onClick={async () => {
                                  if (emailPreview?.id === e.id) { setEmailPreview(null); return; }
                                  try {
                                    const { data } = await http.get(`/admin/email-logs/${e.id}/html`, authCfg());
                                    setEmailPreview({ id: e.id, html: data.html });
                                  } catch { toast({ title: 'Could not load email preview', variant: 'destructive' }); }
                                }}
                                className="text-[10px] tracking-[0.12em] uppercase border border-alp-line px-2.5 py-1 rounded text-alp-gray hover:border-alp-gold hover:text-alp-gold transition-colors">
                                {emailPreview?.id === e.id ? 'Hide' : 'Preview'}
                              </button>
                            </div>
                          </div>
                          <div className="text-alp-gray text-xs mt-1">{e.type} &middot; {new Date(e.createdAt).toLocaleString()} &middot; {e.info}</div>
                          {emailPreview?.id === e.id && (
                            <iframe title="Email preview" srcDoc={emailPreview.html} sandbox="" data-testid="email-preview-frame"
                              className="w-full h-96 mt-4 rounded border border-alp-line bg-white" />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}
          </TabsContent>

          {/* SETTINGS: SHIPPING & BANK */}
          <TabsContent value="settings" className="mt-0">
            {!settingsDraft ? (
              <p className="text-alp-gray py-16 text-center">Loading settings&hellip;</p>
            ) : (
              <div className="max-w-3xl space-y-8">
                <div className="border border-alp-line rounded p-6 bg-alp-card/40" data-testid="sender-settings">
                  <h3 className="font-serif text-xl text-alp-cream mb-1 flex items-center gap-2"><Truck size={18} /> Sender / Return Address (Australia Post)</h3>
                  <p className="text-alp-gray text-sm mb-5">These pre-fill the "Send From" columns of your Australia Post CSV export.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Box label="Business Name"><Input value={settingsDraft.senderBusinessName || ''} onChange={(e) => setf('senderBusinessName', e.target.value)} data-testid="sender-business" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                    <Box label="Contact Name (optional)"><Input value={settingsDraft.senderName || ''} onChange={(e) => setf('senderName', e.target.value)} data-testid="sender-name" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                    <Box label="Address Line 1"><Input value={settingsDraft.senderAddress || ''} onChange={(e) => setf('senderAddress', e.target.value)} data-testid="sender-address" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                    <Box label="Suburb"><Input value={settingsDraft.senderSuburb || ''} onChange={(e) => setf('senderSuburb', e.target.value)} data-testid="sender-suburb" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                    <Box label="State"><Input value={settingsDraft.senderState || ''} onChange={(e) => setf('senderState', e.target.value)} data-testid="sender-state" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                    <Box label="Postcode"><Input value={settingsDraft.senderPostcode || ''} onChange={(e) => setf('senderPostcode', e.target.value)} data-testid="sender-postcode" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                    <Box label="Phone (optional)"><Input value={settingsDraft.senderPhone || ''} onChange={(e) => setf('senderPhone', e.target.value)} data-testid="sender-phone" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                    <Box label="Email (optional)"><Input value={settingsDraft.senderEmail || ''} onChange={(e) => setf('senderEmail', e.target.value)} data-testid="sender-email" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                    <Box label="Parcel Description (on all labels)"><Input value={settingsDraft.parcelDescription || ''} onChange={(e) => setf('parcelDescription', e.target.value)} data-testid="sender-parcel-description" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                  </div>
                  <div className="border-t border-alp-line/60 mt-6 pt-5">
                    <p className="text-[10px] tracking-[0.2em] uppercase text-alp-gold mb-3">Australia Post CSV defaults</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      <Box label="Delivery Service code">
                        <Select value={_svc(settingsDraft.deliveryService)} onValueChange={(v) => setf('deliveryService', v)}>
                          <SelectTrigger data-testid="csv-delivery-service" className="bg-alp-dark border-alp-line text-alp-cream"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="EXP">EXP (Express Post)</SelectItem><SelectItem value="PP">PP (Parcel Post)</SelectItem></SelectContent>
                        </Select>
                      </Box>
                      <Box label="Extra Cover Amount (whole $, blank = none)"><Input value={settingsDraft.extraCoverAmount || ''} onChange={(e) => setf('extraCoverAmount', e.target.value)} data-testid="csv-extra-cover" placeholder="0" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                      <Box label="Signature On Delivery">
                        <Select value={_yn(settingsDraft.signatureOnDelivery)} onValueChange={(v) => setf('signatureOnDelivery', v)}>
                          <SelectTrigger data-testid="csv-signature" className="bg-alp-dark border-alp-line text-alp-cream"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="NO">NO</SelectItem><SelectItem value="YES">YES</SelectItem></SelectContent>
                        </Select>
                      </Box>
                      <Box label="Dangerous Goods">
                        <Select value={_yn(settingsDraft.dangerousGoods)} onValueChange={(v) => setf('dangerousGoods', v)}>
                          <SelectTrigger data-testid="csv-dangerous-goods" className="bg-alp-dark border-alp-line text-alp-cream"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="NO">NO</SelectItem><SelectItem value="YES">YES</SelectItem></SelectContent>
                        </Select>
                      </Box>
                      <Box label="Schedule 8 / Medicinal Cannabis">
                        <Select value={_yn(settingsDraft.schedule8)} onValueChange={(v) => setf('schedule8', v)}>
                          <SelectTrigger data-testid="csv-schedule8" className="bg-alp-dark border-alp-line text-alp-cream"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="NO">NO</SelectItem><SelectItem value="YES">YES</SelectItem></SelectContent>
                        </Select>
                      </Box>
                    </div>
                  </div>
                  <button onClick={saveSettings} data-testid="save-sender-btn" className="mt-5 bg-alp-gold text-alp-black px-6 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors">Save Sender Details</button>
                </div>


                <div className="border border-alp-line rounded p-6 bg-alp-card/40">
                  <h3 className="font-serif text-xl text-alp-cream mb-1">Shipping Settings</h3>
                  <p className="text-alp-gray text-sm mb-5">Set the rates shown at checkout. Orders at or above the free threshold ship free.</p>
                  <div className="grid grid-cols-2 gap-4">
                    <Box label="Standard Shipping ($)"><Input type="number" value={settingsDraft.standardShipping ?? 0} onChange={(e) => setf('standardShipping', e.target.value)} data-testid="settings-standard-shipping" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                    <Box label="Express Shipping ($)"><Input type="number" value={settingsDraft.expressShipping ?? 0} onChange={(e) => setf('expressShipping', e.target.value)} data-testid="settings-express-shipping" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                    <Box label="Free Shipping Threshold ($)"><Input type="number" value={settingsDraft.freeShippingThreshold ?? 0} onChange={(e) => setf('freeShippingThreshold', e.target.value)} data-testid="settings-free-threshold" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                    <Box label="Handling Fee ($)"><Input type="number" value={settingsDraft.handlingFee ?? 0} onChange={(e) => setf('handlingFee', e.target.value)} data-testid="settings-handling-fee" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                  </div>
                  <Box label="Checkout Shipping Text"><Textarea value={settingsDraft.checkoutShippingText || ''} onChange={(e) => setf('checkoutShippingText', e.target.value)} rows={2} data-testid="settings-shipping-text" className="bg-alp-dark border-alp-line text-alp-cream mt-4" /></Box>
                  <div className="flex flex-wrap gap-6 mt-4">
                    <label className="flex items-center gap-2 text-sm text-alp-cream/80"><input type="checkbox" checked={!!settingsDraft.shippingEnabled} onChange={(e) => setf('shippingEnabled', e.target.checked)} data-testid="settings-shipping-enabled" /> Charge shipping</label>
                    <label className="flex items-center gap-2 text-sm text-alp-cream/80"><input type="checkbox" checked={!!settingsDraft.australiaOnly} onChange={(e) => setf('australiaOnly', e.target.checked)} data-testid="settings-australia-only" /> Australia only</label>
                  </div>
                </div>

                <div className="border border-alp-line rounded p-6 bg-alp-card/40">
                  <h3 className="font-serif text-xl text-alp-cream mb-1">Bank Transfer Details</h3>
                  <p className="text-alp-gray text-sm mb-5">Shown to customers on the order confirmation page for manual payment.</p>
                  <div className="grid grid-cols-2 gap-4">
                    <Box label="Account Name"><Input value={settingsDraft.bankAccountName || ''} onChange={(e) => setf('bankAccountName', e.target.value)} data-testid="settings-bank-name" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                    <Box label="BSB"><Input value={settingsDraft.bankBSB || ''} onChange={(e) => setf('bankBSB', e.target.value)} data-testid="settings-bank-bsb" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                    <Box label="Account Number"><Input value={settingsDraft.bankAccountNumber || ''} onChange={(e) => setf('bankAccountNumber', e.target.value)} data-testid="settings-bank-account" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                    <Box label="WhatsApp Number (61...)"><Input value={settingsDraft.whatsappNumber || ''} onChange={(e) => setf('whatsappNumber', e.target.value)} data-testid="settings-whatsapp" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                  </div>
                  <Box label="Payment Reference Instructions"><Textarea value={settingsDraft.bankReferenceText || ''} onChange={(e) => setf('bankReferenceText', e.target.value)} rows={2} data-testid="settings-bank-reference" className="bg-alp-dark border-alp-line text-alp-cream mt-4" /></Box>
                  <Box label="Account Name Note (reassurance shown on order confirmation)"><Textarea value={settingsDraft.bankNameNote || ''} onChange={(e) => setf('bankNameNote', e.target.value)} rows={3} data-testid="settings-bank-name-note" placeholder="e.g. The account name will show as 'Australian Luxe', not 'Australian Luxe Peptides'..." className="bg-alp-dark border-alp-line text-alp-cream mt-4" /></Box>
                  <Box label="Packing Slip Thank-You Note (prints on every packing slip)"><Textarea value={settingsDraft.slipNote || ''} onChange={(e) => setf('slipNote', e.target.value)} rows={2} data-testid="settings-slip-note" placeholder="e.g. Thank you so much for your support! AUSTRALIANLUXE xx" className="bg-alp-dark border-alp-line text-alp-cream mt-4" /></Box>
                  <div className="border border-alp-gold/40 rounded p-4 mt-4 space-y-2" data-testid="settings-card-payment-box">
                    <p className="text-[10px] tracking-[0.2em] uppercase text-alp-gold">Card Payments</p>
                    <label className="flex items-center gap-2 text-sm text-alp-cream/80"><input type="checkbox" checked={!!settingsDraft.cardPaymentEnabled} onChange={(e) => setf('cardPaymentEnabled', e.target.checked)} data-testid="settings-card-payment-enabled" /> Offer &ldquo;Credit / Debit Card&rdquo; as a checkout option</label>
                    <label className="flex items-center gap-2 text-sm text-amber-300/90 mt-3"><input type="checkbox" checked={!!settingsDraft.cardPaymentsPaused} onChange={(e) => setf('cardPaymentsPaused', e.target.checked)} data-testid="settings-card-payments-paused" /> PAUSE Card &amp; Afterpay (buttons greyed out, Bank Transfer only)</label>
                    <label className="block text-xs text-alp-gray mt-2">Card payment link (Square)</label>
                    <Input value={settingsDraft.cardPaymentLink || ''} onChange={(e) => setf('cardPaymentLink', e.target.value)} data-testid="settings-card-payment-link" placeholder="https://square.link/u/..." className="bg-alp-dark border-alp-line text-alp-cream" />
                    <label className="block text-xs text-alp-gray mt-2">Card surcharge / bank fee (%)</label>
                    <Input type="number" step="0.1" value={settingsDraft.cardSurchargePercent ?? ''} onChange={(e) => setf('cardSurchargePercent', e.target.value)} data-testid="settings-card-surcharge" placeholder="2.2" className="bg-alp-dark border-alp-line text-alp-cream" />
                    <label className="flex items-center gap-2 text-sm text-alp-cream/80 mt-3"><input type="checkbox" checked={!!settingsDraft.afterpayEnabled} onChange={(e) => setf('afterpayEnabled', e.target.checked)} data-testid="settings-afterpay-enabled" /> Offer Afterpay as a checkout option</label>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      <div><label className="block text-xs text-alp-gray">Afterpay fee (%)</label><Input type="number" step="0.1" value={settingsDraft.afterpaySurchargePercent ?? ''} onChange={(e) => setf('afterpaySurchargePercent', e.target.value)} data-testid="settings-afterpay-surcharge" placeholder="6" className="bg-alp-dark border-alp-line text-alp-cream" /></div>
                      <div><label className="block text-xs text-alp-gray">Afterpay fixed fee ($)</label><Input type="number" step="0.01" value={settingsDraft.afterpayFixedFee ?? ''} onChange={(e) => setf('afterpayFixedFee', e.target.value)} data-testid="settings-afterpay-fixed" placeholder="0.30" className="bg-alp-dark border-alp-line text-alp-cream" /></div>
                    </div>
                    <p className="text-alp-gray text-[11px]">Customers who pick Card are sent to this link to pay, and are asked to enter their order number as the payment note. Mark the order Paid once the funds arrive (Square doesn't auto-confirm).</p>
                  </div>
                  <div className="border border-alp-gold/40 rounded p-4 mt-4 space-y-2" data-testid="settings-site-gate-box">
                    <p className="text-[10px] tracking-[0.2em] uppercase text-alp-gold">Site Lockdown</p>
                    <label className="flex items-center gap-2 text-sm text-alp-cream/80"><input type="checkbox" checked={!!settingsDraft.siteGateEnabled} onChange={(e) => setf('siteGateEnabled', e.target.checked)} data-testid="settings-site-gate-enabled" /> Require visitors to sign in or register before browsing the website</label>
                    <p className="text-alp-gray text-[11px]">When on, every page (except this admin) shows a luxe sign-in gate. Visitors must log into a customer account and confirm 21+ / research-use-only to enter.</p>
                  </div>
                  <div className="border border-alp-line rounded p-4 mt-4 space-y-3" data-testid="settings-digest-box">
                    <p className="text-[10px] tracking-[0.2em] uppercase text-alp-gold">Weekly Digest Email</p>
                    <label className="flex items-center gap-2 text-sm text-alp-cream/80"><input type="checkbox" checked={!!settingsDraft.digestEnabled} onChange={(e) => setf('digestEnabled', e.target.checked)} data-testid="settings-digest-enabled" /> Email me the financial dashboard insights every Monday morning</label>
                    <div className="flex flex-wrap gap-2">
                      <Input value={settingsDraft.digestEmail || ''} onChange={(e) => setf('digestEmail', e.target.value)} placeholder="you@example.com" data-testid="settings-digest-email" className="bg-alp-dark border-alp-line text-alp-cream flex-1 min-w-[220px]" />
                      <button onClick={sendDigestNow} data-testid="send-digest-now-btn" className="px-4 py-2 text-xs tracking-[0.15em] uppercase border border-alp-gold/50 text-alp-gold rounded hover:bg-alp-gold hover:text-alp-black transition-colors">Send now</button>
                    </div>
                    <p className="text-alp-gray text-[11px]">Save settings first, then Send now to test. Auto-sends Mondays ~8am Sydney time.</p>
                  </div>
                </div>

                <button onClick={saveSettings} data-testid="save-settings-btn" className="bg-alp-gold text-alp-black px-8 py-3 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors">Save Settings</button>
              </div>
            )}
          </TabsContent>
            </main>
          </div>
        </Tabs>
      </div>
      {/* EDIT ORDER ITEMS */}
      <Dialog open={!!custEditOrder} onOpenChange={(o) => !o && setCustEditOrder(null)}>
        <DialogContent className="bg-alp-card border-alp-line text-alp-cream max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-customer-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-alp-cream">Edit customer &mdash; {custEditOrder?.orderNumber}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[['fullName', 'Full name'], ['email', 'Email'], ['phone', 'Phone'], ['address', 'Address'], ['city', 'Suburb / City'], ['state', 'State'], ['postcode', 'Postcode']].map(([k, label]) => (
              <div key={k} className={k === 'address' ? 'sm:col-span-2' : ''}>
                <Label className="text-[10px] tracking-[0.15em] uppercase text-alp-gray mb-1 block">{label}</Label>
                <Input value={custDraft[k] ?? ''} onChange={(e) => setCustDraft((p) => ({ ...p, [k]: e.target.value }))} data-testid={`cust-edit-${k}`} className="bg-alp-dark border-alp-line text-alp-cream h-9" />
              </div>
            ))}
            <div className="sm:col-span-2">
              <Label className="text-[10px] tracking-[0.15em] uppercase text-alp-gray mb-1 block">Delivery notes</Label>
              <Input value={custDraft.notes ?? ''} onChange={(e) => setCustDraft((p) => ({ ...p, notes: e.target.value }))} data-testid="cust-edit-notes" className="bg-alp-dark border-alp-line text-alp-cream h-9" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setCustEditOrder(null)} className="px-4 py-2 border border-alp-line text-alp-cream text-xs tracking-[0.15em] uppercase hover:border-alp-gold transition-colors">Cancel</button>
            <button onClick={saveCustEdit} disabled={custSaving} data-testid="cust-edit-save" className="px-5 py-2 bg-alp-gold text-alp-black text-xs tracking-[0.15em] uppercase font-semibold hover:bg-alp-goldlight transition-colors disabled:opacity-50">{custSaving ? 'Saving\u2026' : 'Save details'}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!itemEditOrder} onOpenChange={(o) => !o && setItemEditOrder(null)}>
        <DialogContent className="bg-alp-card border-alp-line text-alp-cream max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-items-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-alp-cream">Edit items &mdash; {itemEditOrder?.orderNumber}</DialogTitle>
          </DialogHeader>
          {itemEditOrder && (
            <div className="space-y-4">
              <div className="space-y-2">
                {itemEditLines.map((l, idx) => (
                  <div key={`${l.id}-${idx}`} data-testid={`edit-line-${idx}`} className="border border-alp-line rounded p-3 bg-alp-dark/40">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="min-w-0">
                        <div className="text-alp-cream text-sm truncate">{l.name} <span className="text-alp-gray text-xs">{l.size}</span></div>
                        <div className="text-alp-gold text-xs">${Number(l.price).toFixed(2)} each</div>
                      </div>
                      <button onClick={() => itemLineRemove(idx)} data-testid={`edit-line-remove-${idx}`} className="shrink-0 text-alp-gray hover:text-red-400 p-1"><Trash2 size={16} /></button>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="flex items-center border border-alp-line rounded">
                        <button onClick={() => itemLineQty(idx, -1)} data-testid={`edit-line-dec-${idx}`} className="px-3 py-1.5 text-alp-cream hover:text-alp-gold">&minus;</button>
                        <input value={l.qty} onChange={(e) => itemLineSetQty(idx, e.target.value)} data-testid={`edit-line-qty-${idx}`} className="w-12 bg-transparent text-center text-alp-cream outline-none" />
                        <button onClick={() => itemLineQty(idx, 1)} data-testid={`edit-line-inc-${idx}`} className="px-3 py-1.5 text-alp-cream hover:text-alp-gold">+</button>
                      </div>
                      <Select value={l.id} onValueChange={(v) => itemLineSwap(idx, v)}>
                        <SelectTrigger data-testid={`edit-line-swap-${idx}`} className="bg-alp-dark border-alp-line text-alp-cream h-9 flex-1 min-w-[160px]"><SelectValue placeholder="Swap product" /></SelectTrigger>
                        <SelectContent className="bg-alp-card border-alp-line text-alp-cream max-h-64">
                          {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} {p.size ? `(${p.size})` : ''} &mdash; ${Number(p.price).toFixed(2)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
                {itemEditLines.length === 0 && <p className="text-alp-gray text-sm text-center py-3">No items. Add at least one below.</p>}
              </div>

              <div className="flex items-end gap-2 border-t border-alp-line/60 pt-4">
                <div className="flex-1">
                  <Label className="text-[10px] tracking-[0.15em] uppercase text-alp-gray mb-1 block">Add a product</Label>
                  <Select value={itemEditAddId} onValueChange={setItemEditAddId}>
                    <SelectTrigger data-testid="edit-add-product" className="bg-alp-dark border-alp-line text-alp-cream h-9"><SelectValue placeholder="Select a product to add" /></SelectTrigger>
                    <SelectContent className="bg-alp-card border-alp-line text-alp-cream max-h-64">
                      {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} {p.size ? `(${p.size})` : ''} &mdash; ${Number(p.price).toFixed(2)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <button onClick={itemAddProduct} disabled={!itemEditAddId} data-testid="edit-add-btn" className="h-9 px-4 border border-alp-gold/50 text-alp-gold text-xs tracking-[0.15em] uppercase rounded hover:bg-alp-gold hover:text-alp-black transition-colors disabled:opacity-40">Add</button>
              </div>

              <div className="border-t border-alp-line/60 pt-4 text-sm space-y-1">
                <div className="flex justify-between text-alp-gray"><span>New subtotal</span><span className="text-alp-cream" data-testid="edit-subtotal">${itemEditSubtotal.toFixed(2)}</span></div>
                {itemEditOrder.discountCode ? <div className="flex justify-between text-alp-gray"><span>Discount ({itemEditOrder.discountCode})</span><span>recalculated on save</span></div> : null}
                <div className="flex justify-between text-alp-gray"><span>Shipping</span><span>recalculated on save</span></div>
                {itemEditOrder.inventoryDeducted ? <p className="text-alp-gold/80 text-[11px] pt-1">This order's stock was already deducted &mdash; inventory will be adjusted to match your changes.</p> : null}
              </div>

              <label className="flex items-center gap-2 text-sm text-alp-cream/80 cursor-pointer">
                <input type="checkbox" checked={itemEditNotify} onChange={(e) => setItemEditNotify(e.target.checked)} data-testid="edit-notify-customer" className="accent-alp-gold" />
                Email the customer an updated order summary
              </label>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setItemEditOrder(null)} className="px-5 py-2.5 text-xs tracking-[0.15em] uppercase text-alp-gray border border-alp-line rounded hover:text-alp-cream transition-colors">Cancel</button>
            <button onClick={saveItemEdits} disabled={itemEditSaving || itemEditLines.length === 0} data-testid="save-items-btn" className="px-6 py-2.5 text-xs tracking-[0.15em] uppercase bg-alp-gold text-alp-black font-semibold rounded hover:bg-alp-goldlight transition-colors disabled:opacity-50">{itemEditSaving ? 'Saving...' : 'Save Changes'}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-alp-card border-alp-line text-alp-cream max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-alp-cream">{editing?.id ? 'Edit Product' : 'New Product'}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Box label="Name"><Input value={editing.name} onChange={(e) => ef('name', e.target.value)} className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                <Box label="Code"><Input value={editing.code} onChange={(e) => ef('code', e.target.value)} className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                <Box label="Size"><Input value={editing.size} onChange={(e) => ef('size', e.target.value)} className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                <Box label="Price (AUD)"><Input type="number" value={editing.price} onChange={(e) => ef('price', parseFloat(e.target.value) || 0)} className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                <Box label="Compare-at / Was Price (AUD)"><Input type="number" value={editing.compareAtPrice} onChange={(e) => ef('compareAtPrice', parseFloat(e.target.value) || 0)} className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                <Box label="Inventory (stock count)"><Input type="number" value={editing.inventory} onChange={(e) => ef('inventory', parseInt(e.target.value) || 0)} className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                <Box label="Supplier Cost (AUD)"><Input type="number" value={editing.supplierCost ?? 0} onChange={(e) => ef('supplierCost', parseFloat(e.target.value) || 0)} data-testid="product-supplier-cost" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                <Box label="Extra Cost Allocation (AUD)"><Input type="number" value={editing.extraCost ?? 0} onChange={(e) => ef('extraCost', parseFloat(e.target.value) || 0)} data-testid="product-extra-cost" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                <Box label="Parcel Weight (kg) — for Australia Post"><Input type="number" step="0.01" value={editing.weight ?? 0} onChange={(e) => ef('weight', parseFloat(e.target.value) || 0)} data-testid="product-weight" placeholder="e.g. 0.05" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                <Box label="Category">
                  <Select value={editing.category} onValueChange={(v) => ef('category', v)}>
                    <SelectTrigger className="bg-alp-dark border-alp-line text-alp-cream"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-alp-card border-alp-line text-alp-cream">{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </Box>
                <Box label="Stock Status">
                  <Select value={editing.stockStatus} onValueChange={(v) => ef('stockStatus', v)}>
                    <SelectTrigger className="bg-alp-dark border-alp-line text-alp-cream"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-alp-card border-alp-line text-alp-cream">{STOCK_STATUSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </Box>
                <Box label="Ribbon (e.g. Best Seller)"><Input value={editing.ribbon} onChange={(e) => ef('ribbon', e.target.value)} placeholder="Optional" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
              </div>
              <div className="grid grid-cols-3 gap-3 border border-alp-line rounded p-3 bg-alp-dark/40" data-testid="product-profit-summary">
                {(() => {
                  const cogs = (Number(editing.supplierCost) || 0) + (Number(editing.extraCost) || 0);
                  const sp = Number(editing.price) || 0;
                  const gp = sp - cogs;
                  const margin = sp > 0 ? (gp / sp) * 100 : 0;
                  return (
                    <>
                      <div><div className="text-[10px] tracking-[0.12em] uppercase text-alp-gray">Total COGS</div><div className="text-alp-cream text-sm mt-1">${cogs.toFixed(2)}</div></div>
                      <div><div className="text-[10px] tracking-[0.12em] uppercase text-alp-gray">Gross Profit</div><div className="text-alp-gold text-sm mt-1" data-testid="product-gross-profit">${gp.toFixed(2)}</div></div>
                      <div><div className="text-[10px] tracking-[0.12em] uppercase text-alp-gray">Margin</div><div className="text-alp-gold text-sm mt-1" data-testid="product-margin">{margin.toFixed(1)}%</div></div>
                    </>
                  );
                })()}
              </div>
              <Box label="Product Image (optional)">
                <Input value={editing.image} onChange={(e) => ef('image', e.target.value)} placeholder="Paste image URL or upload below" data-testid="product-image-url" className="bg-alp-dark border-alp-line text-alp-cream" />
                <div className="flex items-center gap-4 mt-3">
                  {editing.image ? (
                    <img src={editing.image} alt="preview" data-testid="product-image-preview" className="w-16 h-16 object-cover rounded bg-alp-dark border border-alp-line" />
                  ) : (
                    <div className="w-16 h-16 rounded bg-alp-dark border border-alp-line flex items-center justify-center text-alp-gray text-[10px]">No image</div>
                  )}
                  <label className="inline-flex items-center gap-2 cursor-pointer text-xs tracking-[0.15em] uppercase border border-alp-line px-4 py-2 rounded text-alp-cream hover:border-alp-gold hover:text-alp-gold transition-colors">
                    <Upload size={14} /> {prodImgUploading ? 'Uploading\u2026' : 'Upload Image'}
                    <input type="file" accept="image/*" className="hidden" data-testid="product-image-upload" disabled={prodImgUploading} onChange={(e) => uploadProductImage(e.target.files?.[0])} />
                  </label>
                  {editing.image ? (
                    <button type="button" onClick={() => ef('image', '')} data-testid="product-image-clear" className="text-xs tracking-[0.12em] uppercase text-alp-gray hover:text-red-400 transition-colors">Clear</button>
                  ) : null}
                </div>
              </Box>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs tracking-[0.15em] uppercase text-alp-gray">Description</Label>
                  <button onClick={aiFillProduct} disabled={aiGenBusy} data-testid="ai-gen-product-btn" className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] tracking-[0.12em] uppercase border border-alp-gold/50 text-alp-gold hover:bg-alp-gold hover:text-alp-black transition-colors disabled:opacity-50">
                    <Sparkles size={11} className={aiGenBusy ? 'animate-spin' : ''} /> {aiGenBusy ? 'Claude is writing...' : 'Generate with Claude'}
                  </button>
                </div>
                <Textarea value={editing.description} onChange={(e) => ef('description', e.target.value)} rows={3} className="bg-alp-dark border-alp-line text-alp-cream" />
                <p className="text-alp-gray text-[10px] mt-1">Claude also fills the SEO title + meta description below.</p>
              </div>
              <Box label="Certificate of Analysis (COA) — PDF, view-only for customers">
                {!editing.id ? (
                  <p className="text-xs text-alp-gray/70" data-testid="coa-save-first-note">Save the product first, then re-open it to upload a COA.</p>
                ) : (
                  <div className="flex items-center gap-4 flex-wrap">
                    {editing.coaImages?.length > 0 ? (
                      <span className="inline-flex items-center gap-2 text-xs tracking-[0.12em] uppercase text-alp-gold" data-testid="coa-status">
                        <CheckCircle2 size={14} /> COA uploaded &middot; {editing.coaImages.length} page{editing.coaImages.length === 1 ? '' : 's'}
                      </span>
                    ) : (
                      <span className="text-xs text-alp-gray" data-testid="coa-status">No COA uploaded</span>
                    )}
                    <label className="inline-flex items-center gap-2 cursor-pointer text-xs tracking-[0.15em] uppercase border border-alp-line px-4 py-2 rounded text-alp-cream hover:border-alp-gold hover:text-alp-gold transition-colors">
                      <Upload size={14} /> {coaUploading ? 'Processing\u2026' : (editing.coaImages?.length ? 'Replace COA' : 'Upload COA (PDF)')}
                      <input type="file" accept="application/pdf,.pdf" className="hidden" data-testid="coa-upload-input" disabled={coaUploading} onChange={(e) => uploadCoa(e.target.files?.[0])} />
                    </label>
                    {editing.coaImages?.length > 0 ? (
                      <button type="button" onClick={removeCoa} data-testid="coa-remove-btn" className="text-xs tracking-[0.12em] uppercase text-alp-gray hover:text-red-400 transition-colors">Remove</button>
                    ) : null}
                  </div>
                )}
                <p className="text-[11px] text-alp-gray/60 mt-2">The PDF is converted to watermarked images so it can be viewed but not downloaded. Customers see a "View COA" button on the product page.</p>
              </Box>
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center gap-2 text-sm text-alp-cream/80"><input type="checkbox" checked={editing.coa} onChange={(e) => ef('coa', e.target.checked)} /> COA Available</label>
                <label className="flex items-center gap-2 text-sm text-alp-cream/80"><input type="checkbox" checked={editing.featured} onChange={(e) => ef('featured', e.target.checked)} /> Featured</label>
                <label className="flex items-center gap-2 text-sm text-alp-cream/80"><input type="checkbox" data-testid="product-addon-checkbox" checked={editing.addon} onChange={(e) => ef('addon', e.target.checked)} /> Add-on at checkout</label>
                <label className="flex items-center gap-2 text-sm text-alp-cream/80"><input type="checkbox" data-testid="product-bundle-checkbox" checked={editing.bundle} onChange={(e) => ef('bundle', e.target.checked)} /> Bundle (multiple products)</label>
              </div>
              {editing.bundle && (
                <div className="border border-alp-gold/30 rounded-lg p-4 bg-alp-dark/40" data-testid="bundle-builder">
                  <p className="text-[11px] tracking-[0.2em] uppercase text-alp-gold mb-3">Bundle contents</p>
                  <div className="flex flex-wrap items-end gap-3 mb-3">
                    <div className="flex-1 min-w-[200px]">
                      <Select value={bundlePick.productId} onValueChange={(v) => setBundlePick((s) => ({ ...s, productId: v }))}>
                        <SelectTrigger data-testid="bundle-product-select" className="bg-alp-dark border-alp-line text-alp-cream"><SelectValue placeholder="Choose a product to add" /></SelectTrigger>
                        <SelectContent>
                          {products.filter((p) => !p.bundle && p.id !== editing.id).map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}{p.size ? ` (${p.size})` : ''} — ${Number(p.price).toFixed(2)}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Input type="number" min="1" value={bundlePick.qty} onChange={(e) => setBundlePick((s) => ({ ...s, qty: e.target.value }))} data-testid="bundle-qty" className="w-20 bg-alp-dark border-alp-line text-alp-cream" />
                    <button type="button" onClick={addBundleItem} data-testid="bundle-add-item" className="inline-flex items-center gap-1.5 border border-alp-gold/50 text-alp-gold px-4 py-2 rounded text-xs tracking-[0.12em] uppercase hover:bg-alp-gold hover:text-alp-black transition-colors"><Plus size={14} /> Add</button>
                  </div>
                  {(editing.bundleItems || []).length === 0 ? (
                    <p className="text-alp-gray text-xs">No products in this bundle yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {(editing.bundleItems || []).map((bi, idx) => {
                        const p = products.find((x) => x.id === bi.productId);
                        return (
                          <div key={idx} data-testid={`bundle-item-${bi.productId}`} className="flex items-center justify-between bg-alp-card/60 rounded px-3 py-2 text-sm">
                            <span className="text-alp-cream">{p ? p.name : bi.productId} <span className="text-alp-gray text-xs">{p?.size} × {bi.qty}</span></span>
                            <div className="flex items-center gap-3">
                              <span className="text-alp-gray text-xs">{p ? `$${(Number(p.price) * bi.qty).toFixed(2)}` : ''}</span>
                              <button type="button" onClick={() => ef('bundleItems', editing.bundleItems.filter((_, j) => j !== idx))} data-testid={`bundle-remove-${bi.productId}`} className="text-alp-gray hover:text-red-400"><Trash2 size={14} /></button>
                            </div>
                          </div>
                        );
                      })}
                      <div className="flex items-center justify-between pt-2 border-t border-alp-line/60 text-sm">
                        <span className="text-alp-gray">Regular total (sum of items)</span>
                        <span className="text-alp-cream" data-testid="bundle-regular-total">${bundleRegularTotal.toFixed(2)}</span>
                      </div>
                      <p className="text-[11px] text-alp-gray/70">Set the discounted <span className="text-alp-cream">Price</span> above; the regular total auto-fills the "Was price" so customers see the saving. Selling a bundle deducts each item's stock.</p>
                    </div>
                  )}
                </div>
              )}
              <div className="border-t border-alp-line/60 pt-4 mt-2">
                <p className="text-[10px] tracking-[0.2em] uppercase text-alp-gold mb-3">Search Engine (SEO)</p>
                <Box label="SEO Title (optional — falls back to product name)"><Input value={editing.seoTitle || ''} onChange={(e) => ef('seoTitle', e.target.value)} data-testid="product-seo-title" placeholder={`${editing.name || 'Product'} | Australian Luxe Peptides`} className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                <Box label="Meta Description (optional, ~155 chars)"><Textarea value={editing.seoDescription || ''} onChange={(e) => ef('seoDescription', e.target.value)} rows={2} data-testid="product-seo-description" className="bg-alp-dark border-alp-line text-alp-cream mt-4" /></Box>
                <p className="text-xs text-alp-gray/60 mt-1">{(editing.seoDescription || '').length} characters</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setEditing(null)} className="px-5 py-2.5 text-xs tracking-[0.15em] uppercase text-alp-gray border border-alp-line rounded hover:text-alp-cream transition-colors">Cancel</button>
            <button onClick={saveProduct} className="px-6 py-2.5 text-xs tracking-[0.15em] uppercase bg-alp-gold text-alp-black font-semibold rounded hover:bg-alp-goldlight transition-colors">Save Product</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CONTACT REPLY DIALOG */}
      <Dialog open={!!replyingContact} onOpenChange={(o) => !o && setReplyingContact(null)}>
        <DialogContent className="bg-alp-card border-alp-line max-w-lg" data-testid="reply-dialog">
          <DialogHeader>
            <DialogTitle className="text-alp-cream font-serif text-2xl">Reply to {replyingContact?.name || 'enquiry'}</DialogTitle>
          </DialogHeader>
          {replyingContact && (
            <div className="space-y-4">
              <div className="text-xs text-alp-gray">To: <span className="text-alp-cream">{replyingContact.email}</span></div>
              <div className="bg-alp-dark/60 rounded p-3 text-xs text-alp-gray max-h-24 overflow-y-auto whitespace-pre-wrap">{replyingContact.message}</div>
              {!emailStatus?.connected && (
                <p className="text-[11px] text-alp-gold/80" data-testid="reply-smtp-warning">Note: email sending is not active in this preview. Your reply will be saved and sent for real once the live site is used.</p>
              )}
              <Box label="Subject">
                <Input value={replySubject} onChange={(e) => setReplySubject(e.target.value)} data-testid="reply-subject" className="bg-alp-dark border-alp-line text-alp-cream" />
              </Box>
              <Box label="Message">
                <Textarea value={replyMessage} onChange={(e) => setReplyMessage(e.target.value)} rows={6} data-testid="reply-message" placeholder="Write your reply\u2026" className="bg-alp-dark border-alp-line text-alp-cream" />
              </Box>
              <Box label="Attachments (PDF or images, max 15MB total)">
                <label className="inline-flex items-center gap-2 cursor-pointer text-xs tracking-[0.15em] uppercase border border-alp-line px-4 py-2 rounded text-alp-cream hover:border-alp-gold hover:text-alp-gold transition-colors">
                  <Paperclip size={14} /> Add files
                  <input type="file" accept="application/pdf,.pdf,image/*" multiple className="hidden" data-testid="reply-attachments" onChange={(e) => setReplyFiles(Array.from(e.target.files || []))} />
                </label>
                {replyFiles.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="reply-file-list">
                    {replyFiles.map((f, i) => (
                      <span key={i} className="inline-flex items-center gap-1.5 text-[11px] text-alp-gold border border-alp-gold/30 rounded px-2.5 py-1">
                        <Paperclip size={11} /> {f.name}
                        <button type="button" onClick={() => setReplyFiles((prev) => prev.filter((_, j) => j !== i))} className="hover:text-red-400"><X size={12} /></button>
                      </span>
                    ))}
                  </div>
                )}
              </Box>
              <Box label="Attach a stored product COA (optional)">
                <Select value={replyCoaId || 'none'} onValueChange={(v) => setReplyCoaId(v === 'none' ? '' : v)}>
                  <SelectTrigger data-testid="reply-coa-select" className="bg-alp-dark border-alp-line text-alp-cream"><SelectValue placeholder="No COA" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No COA</SelectItem>
                    {products.filter((p) => p.coaImages?.length > 0).map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}{p.size ? ` (${p.size})` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {products.filter((p) => p.coaImages?.length > 0).length === 0 && (
                  <p className="text-[11px] text-alp-gray/60 mt-2">No products have a stored COA yet. Upload one from the product editor.</p>
                )}
                {replyCoaId && (
                  <p className="text-[11px] text-alp-gold mt-2 inline-flex items-center gap-1.5" data-testid="reply-coa-selected"><Paperclip size={11} /> COA will be attached as a watermarked PDF.</p>
                )}
              </Box>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setReplyingContact(null)} className="text-xs tracking-[0.15em] uppercase text-alp-gray hover:text-alp-cream px-4 py-2.5 transition-colors">Cancel</button>
            <button onClick={sendReply} disabled={replySending || !replyMessage.trim()} data-testid="reply-send-btn" className="inline-flex items-center gap-2 bg-alp-gold text-alp-black px-6 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors disabled:opacity-50">
              <Send size={14} /> {replySending ? 'Sending\u2026' : 'Send Reply'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* DISCOUNT EDIT DIALOG */}
      <Dialog open={!!editingDiscount} onOpenChange={(o) => !o && setEditingDiscount(null)}>
        <DialogContent className="bg-alp-card border-alp-line text-alp-cream max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-alp-cream">{editingDiscount?.id ? 'Edit Discount' : 'New Discount'}</DialogTitle>
          </DialogHeader>
          {editingDiscount && (
            <div className="space-y-4">
              <Box label="Code (e.g. LUXE10)"><Input value={editingDiscount.code} onChange={(e) => df('code', e.target.value.toUpperCase())} data-testid="discount-code-field" className="bg-alp-dark border-alp-line text-alp-cream uppercase" /></Box>
              <div className="grid grid-cols-2 gap-4">
                <Box label="Type">
                  <Select value={editingDiscount.type} onValueChange={(v) => df('type', v)}>
                    <SelectTrigger data-testid="discount-type-field" className="bg-alp-dark border-alp-line text-alp-cream"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-alp-card border-alp-line text-alp-cream">
                      <SelectItem value="percentage">Percentage (%)</SelectItem>
                      <SelectItem value="fixed">Fixed amount ($)</SelectItem>
                      <SelectItem value="freeItem">Free item (gift with purchase)</SelectItem>
                    </SelectContent>
                  </Select>
                </Box>
                {editingDiscount.type === 'freeItem' ? (
                  <Box label="Free product">
                    <Select value={editingDiscount.freeProductId || ''} onValueChange={(v) => df('freeProductId', v)}>
                      <SelectTrigger data-testid="discount-free-product-field" className="bg-alp-dark border-alp-line text-alp-cream"><SelectValue placeholder="Choose a product…" /></SelectTrigger>
                      <SelectContent className="bg-alp-card border-alp-line text-alp-cream max-h-64">
                        {products.filter((p) => !p.hidden && !p.bundle).map((p) => (
                          <SelectItem key={p.id} value={p.id}>{p.name} {p.size}{p.digital ? ' (digital)' : ''}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Box>
                ) : (
                  <Box label={editingDiscount.type === 'percentage' ? 'Amount (%)' : 'Amount ($)'}><Input type="number" value={editingDiscount.amount} onChange={(e) => df('amount', e.target.value)} data-testid="discount-amount-field" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Box label="Minimum Order ($)"><Input type="number" value={editingDiscount.minOrder} onChange={(e) => df('minOrder', e.target.value)} className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                <Box label="Usage Limit (blank = unlimited)"><Input type="number" value={editingDiscount.usageLimit} onChange={(e) => df('usageLimit', e.target.value)} className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
              </div>
              <Box label="Expiry Date (optional)"><Input type="date" value={editingDiscount.expiry || ''} onChange={(e) => df('expiry', e.target.value)} className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
              <Box label="Lock to customer email (optional — only this email can use it)"><Input type="email" value={editingDiscount.restrictEmail || ''} onChange={(e) => df('restrictEmail', e.target.value)} data-testid="discount-restrict-email-field" placeholder="customer@email.com" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
              <Box label="Deal Description (shown to VIPs, optional)"><Input value={editingDiscount.description || ''} onChange={(e) => df('description', e.target.value)} data-testid="discount-description-field" placeholder="e.g. Exclusive 15% off for library members" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center gap-2 text-sm text-alp-cream/80"><input type="checkbox" checked={editingDiscount.active} onChange={(e) => df('active', e.target.checked)} data-testid="discount-active-field" /> Active</label>
                <label className="flex items-center gap-2 text-sm text-alp-cream/80"><input type="checkbox" checked={!!editingDiscount.vip} onChange={(e) => df('vip', e.target.checked)} data-testid="discount-vip-field" /> VIP only (show in Library Deals)</label>
                <label className="flex items-center gap-2 text-sm text-alp-cream/80"><input type="checkbox" checked={!!editingDiscount.affiliate} onChange={(e) => df('affiliate', e.target.checked)} data-testid="discount-affiliate-field" /> Affiliate code</label>
              </div>
              {editingDiscount.affiliate && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Box label="Affiliate / Referrer Name (who to reward)"><Input value={editingDiscount.affiliateName || ''} onChange={(e) => df('affiliateName', e.target.value)} data-testid="discount-affiliate-name-field" placeholder="e.g. @jess_fit or Jess Smith" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                  <Box label="Commission % (of paid sales)"><Input type="number" value={editingDiscount.commissionPercent ?? 0} onChange={(e) => df('commissionPercent', e.target.value)} data-testid="discount-commission-field" placeholder="e.g. 10" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                  <Box label="Affiliate account email (lets them see their own stats + receive credit)"><Input type="email" value={editingDiscount.affiliateEmail || ''} onChange={(e) => df('affiliateEmail', e.target.value)} data-testid="discount-affiliate-email-field" placeholder="their customer account email" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setEditingDiscount(null)} className="px-5 py-2.5 text-xs tracking-[0.15em] uppercase text-alp-gray border border-alp-line rounded hover:text-alp-cream transition-colors">Cancel</button>
            <button onClick={saveDiscount} data-testid="save-discount-btn" className="px-6 py-2.5 text-xs tracking-[0.15em] uppercase bg-alp-gold text-alp-black font-semibold rounded hover:bg-alp-goldlight transition-colors">Save Discount</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PROMOTION EDIT DIALOG */}
      <Dialog open={!!editingPromo} onOpenChange={(o) => !o && setEditingPromo(null)}>
        <DialogContent className="bg-alp-card border-alp-line text-alp-cream max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-alp-cream">{editingPromo?.id ? 'Edit Promotion' : 'New Promotion'}</DialogTitle>
          </DialogHeader>
          {editingPromo && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Box label="Type">
                  <Select value={editingPromo.type} onValueChange={(v) => pf('type', v)}>
                    <SelectTrigger data-testid="promo-type-field" className="bg-alp-dark border-alp-line text-alp-cream"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-alp-card border-alp-line text-alp-cream">{PROMO_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </Box>
                <Box label="Promotion Name"><Input value={editingPromo.name} onChange={(e) => pf('name', e.target.value)} data-testid="promo-name-field" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
              </div>
              <button onClick={aiFillPromo} disabled={aiGenBusy} data-testid="ai-gen-promo-btn" className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-lg text-[11px] tracking-[0.15em] uppercase border border-alp-gold/50 text-alp-gold hover:bg-alp-gold hover:text-alp-black transition-colors disabled:opacity-50">
                <Sparkles size={13} className={aiGenBusy ? 'animate-spin' : ''} /> {aiGenBusy ? 'Claude is writing...' : 'Write promo copy with Claude'}
              </button>
              <Box label="Description (internal)"><Textarea value={editingPromo.description} onChange={(e) => pf('description', e.target.value)} rows={2} className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
              <Box label="Customer-facing Promo Text"><Textarea value={editingPromo.promoText} onChange={(e) => pf('promoText', e.target.value)} rows={2} data-testid="promo-text-field" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
              <Box label="Image URL (optional)"><Input value={editingPromo.image} onChange={(e) => pf('image', e.target.value)} placeholder="https://..." data-testid="promo-image-field" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
              <div className="grid grid-cols-2 gap-4">
                <Box label="Button Text (optional)"><Input value={editingPromo.buttonText} onChange={(e) => pf('buttonText', e.target.value)} placeholder="Shop Now" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                <Box label="Button Link (optional)"><Input value={editingPromo.buttonLink} onChange={(e) => pf('buttonLink', e.target.value)} placeholder="/shop" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                <Box label="Start Date"><Input type="date" value={editingPromo.startDate || ''} onChange={(e) => pf('startDate', e.target.value)} data-testid="promo-start-field" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                <Box label="End Date"><Input type="date" value={editingPromo.endDate || ''} onChange={(e) => pf('endDate', e.target.value)} data-testid="promo-end-field" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
              </div>
              <label className="flex items-center gap-2 text-sm text-alp-cream/80"><input type="checkbox" checked={editingPromo.active} onChange={(e) => pf('active', e.target.checked)} data-testid="promo-active-field" /> Active</label>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setEditingPromo(null)} className="px-5 py-2.5 text-xs tracking-[0.15em] uppercase text-alp-gray border border-alp-line rounded hover:text-alp-cream transition-colors">Cancel</button>
            <button onClick={savePromo} data-testid="save-promo-btn" className="px-6 py-2.5 text-xs tracking-[0.15em] uppercase bg-alp-gold text-alp-black font-semibold rounded hover:bg-alp-goldlight transition-colors">Save Promotion</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* UPCOMING PRODUCT DIALOG */}
      <Dialog open={!!editingIncoming} onOpenChange={(o) => !o && setEditingIncoming(null)}>
        <DialogContent className="bg-alp-card border-alp-line text-alp-cream max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-alp-cream">{editingIncoming?.id ? 'Edit Upcoming Product' : 'Add Upcoming Product'}</DialogTitle>
            <p className="text-alp-gray text-xs">Shown on the site as coming soon. Link a product and expected qty to include it in the sales forecast.</p>
          </DialogHeader>
          {editingIncoming && (
            <div className="space-y-4">
              <Box label="Product Name"><Input value={editingIncoming.name} onChange={(e) => inf('name', e.target.value)} data-testid="incoming-name-field" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
              <Box label="Note / Description"><Textarea value={editingIncoming.note} onChange={(e) => inf('note', e.target.value)} rows={2} data-testid="incoming-note-field" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
              <div className="grid grid-cols-2 gap-4">
                <Box label="ETA (free text)"><Input value={editingIncoming.eta} onChange={(e) => inf('eta', e.target.value)} placeholder="e.g. Mid-July 2026" data-testid="incoming-eta-field" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                <Box label="Image URL (optional)"><Input value={editingIncoming.image} onChange={(e) => inf('image', e.target.value)} placeholder="https://..." className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Box label="Link to Product (for forecast)">
                  <Select value={editingIncoming.productId || 'none'} onValueChange={(v) => inf('productId', v === 'none' ? '' : v)}>
                    <SelectTrigger data-testid="incoming-product-field" className="bg-alp-dark border-alp-line text-alp-cream"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-alp-card border-alp-line text-alp-cream max-h-64">
                      <SelectItem value="none">Not linked</SelectItem>
                      {products.filter((p) => !p.digital && !p.bundle).map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name}{p.size ? ` (${p.size})` : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Box>
                <Box label="Expected Qty (units coming)"><Input type="number" min="0" value={editingIncoming.qty ?? 0} onChange={(e) => inf('qty', Number(e.target.value) || 0)} data-testid="incoming-qty-field" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
              </div>
              <label className="flex items-center gap-2 text-sm text-alp-cream/80"><input type="checkbox" checked={editingIncoming.active} onChange={(e) => inf('active', e.target.checked)} data-testid="incoming-active-field" /> Active</label>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setEditingIncoming(null)} className="px-5 py-2.5 text-xs tracking-[0.15em] uppercase text-alp-gray border border-alp-line rounded hover:text-alp-cream transition-colors">Cancel</button>
            <button onClick={saveIncoming} data-testid="save-incoming-btn" className="px-6 py-2.5 text-xs tracking-[0.15em] uppercase bg-alp-gold text-alp-black font-semibold rounded hover:bg-alp-goldlight transition-colors">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* REVIEW DIALOG */}
      <Dialog open={!!editingReview} onOpenChange={(o) => !o && setEditingReview(null)}>
        <DialogContent className="bg-alp-card border-alp-line text-alp-cream max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-alp-cream">{editingReview?.id ? 'Edit Review' : 'Add Review'}</DialogTitle>
          </DialogHeader>
          {editingReview && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Box label="Reviewer Name"><Input value={editingReview.name} onChange={(e) => rvf('name', e.target.value)} data-testid="review-name-field" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                <div>
                  <Label className="text-xs tracking-[0.15em] uppercase text-alp-gray mb-2 block">Rating</Label>
                  <div className="flex items-center gap-1.5 mt-2" data-testid="review-rating-field">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button key={n} type="button" onClick={() => rvf('rating', n)} className="p-0.5">
                        <Star size={22} className={n <= editingReview.rating ? 'text-alp-gold fill-alp-gold' : 'text-alp-line hover:text-alp-gold/50'} />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <Box label="Review Text"><Textarea value={editingReview.body} onChange={(e) => rvf('body', e.target.value)} rows={4} data-testid="review-body-field" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
              <div className="grid grid-cols-2 gap-4">
                <Box label="Product (optional)"><Input value={editingReview.productName} onChange={(e) => rvf('productName', e.target.value)} placeholder="e.g. BPC-157 10mg" data-testid="review-product-field" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                <Box label="Date (optional)"><Input value={editingReview.date} onChange={(e) => rvf('date', e.target.value)} placeholder="e.g. June 2026" data-testid="review-date-field" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
              </div>
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center gap-2 text-sm text-alp-cream/80"><input type="checkbox" checked={editingReview.featured} onChange={(e) => rvf('featured', e.target.checked)} data-testid="review-featured-field" /> Feature on homepage</label>
                <label className="flex items-center gap-2 text-sm text-alp-cream/80"><input type="checkbox" checked={editingReview.active} onChange={(e) => rvf('active', e.target.checked)} data-testid="review-active-field" /> Visible</label>
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setEditingReview(null)} className="px-5 py-2.5 text-xs tracking-[0.15em] uppercase text-alp-gray border border-alp-line rounded hover:text-alp-cream transition-colors">Cancel</button>
            <button onClick={saveReview} data-testid="save-review-btn" className="px-6 py-2.5 text-xs tracking-[0.15em] uppercase bg-alp-gold text-alp-black font-semibold rounded hover:bg-alp-goldlight transition-colors">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* LIBRARY ITEM DIALOG */}
      <Dialog open={!!editingLibItem} onOpenChange={(o) => !o && setEditingLibItem(null)}>
        <DialogContent className="bg-alp-card border-alp-line text-alp-cream max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-alp-cream">{editingLibItem?.id ? 'Edit Guide' : 'New Guide'}</DialogTitle>
          </DialogHeader>
          {editingLibItem && (
            <div className="space-y-4">
              <button onClick={aiFillLibGuide} disabled={aiGenBusy} data-testid="ai-gen-library-btn" className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-lg text-[11px] tracking-[0.15em] uppercase border border-alp-gold/50 text-alp-gold hover:bg-alp-gold hover:text-alp-black transition-colors disabled:opacity-50">
                <Sparkles size={13} className={aiGenBusy ? 'animate-spin' : ''} /> {aiGenBusy ? 'Claude is drafting the guide...' : 'Generate full guide with Claude'}
              </button>
              <Box label="Link to Product (auto-fills title)">
                <Select value={editingLibItem.productId || 'none'} onValueChange={(v) => {
                  if (v === 'none') { lif('productId', ''); return; }
                  const prod = products.find((p) => p.id === v);
                  setEditingLibItem((prev) => ({ ...prev, productId: v, title: prev.title || (prod ? prod.name : ''), category: prev.category === 'General' && prod?.category ? prod.category : prev.category }));
                }}>
                  <SelectTrigger data-testid="lib-item-product" className="bg-alp-dark border-alp-line text-alp-cream"><SelectValue placeholder="Select a product (optional)" /></SelectTrigger>
                  <SelectContent className="bg-alp-card border-alp-line text-alp-cream max-h-64">
                    <SelectItem value="none">No linked product</SelectItem>
                    {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Box>
              <div className="grid grid-cols-2 gap-4">
                <Box label="Title"><Input value={editingLibItem.title} onChange={(e) => lif('title', e.target.value)} data-testid="lib-item-title" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                <Box label="Category"><Input value={editingLibItem.category} onChange={(e) => lif('category', e.target.value)} placeholder="e.g. Reconstitution" data-testid="lib-item-category" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
              </div>
              <Box label="What it is"><Textarea value={editingLibItem.whatItIs} onChange={(e) => lif('whatItIs', e.target.value)} rows={2} data-testid="lib-item-whatitis" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
              <Box label="Use"><Textarea value={editingLibItem.use} onChange={(e) => lif('use', e.target.value)} rows={2} data-testid="lib-item-use" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
              <div className="grid grid-cols-2 gap-4">
                <Box label="Recommended BAC Water"><Input value={editingLibItem.bacWater} onChange={(e) => lif('bacWater', e.target.value)} placeholder="e.g. 2mL" data-testid="lib-item-bacwater" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                <Box label="Dosage"><Input value={editingLibItem.dosage} onChange={(e) => lif('dosage', e.target.value)} placeholder="e.g. 250mcg" data-testid="lib-item-dosage" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                <Box label="Timing"><Input value={editingLibItem.timing} onChange={(e) => lif('timing', e.target.value)} placeholder="e.g. AM, before food" data-testid="lib-item-timing" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
                <Box label="Cycle"><Input value={editingLibItem.cycle} onChange={(e) => lif('cycle', e.target.value)} placeholder="e.g. 8 weeks on, 4 off" data-testid="lib-item-cycle" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
              </div>
              <Box label="Additional Notes (optional)"><Textarea value={editingLibItem.body} onChange={(e) => lif('body', e.target.value)} rows={3} data-testid="lib-item-body" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
              <Box label="Image URL (optional)"><Input value={editingLibItem.image} onChange={(e) => lif('image', e.target.value)} placeholder="https://..." data-testid="lib-item-image" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
              <div className="flex flex-wrap items-center gap-4">
                <label className="inline-flex items-center gap-2 cursor-pointer text-xs tracking-[0.15em] uppercase border border-alp-line px-4 py-2 rounded text-alp-cream hover:border-alp-gold hover:text-alp-gold transition-colors">
                  <Upload size={14} /> {libImgUploading ? 'Uploading\u2026' : 'Upload Image'}
                  <input type="file" accept="image/*" className="hidden" data-testid="lib-item-upload" disabled={libImgUploading} onChange={(e) => uploadLibImage(e.target.files?.[0])} />
                </label>
                <Box label="Order"><Input type="number" value={editingLibItem.order} onChange={(e) => lif('order', e.target.value)} className="bg-alp-dark border-alp-line text-alp-cream w-24" /></Box>
                <label className="flex items-center gap-2 text-sm text-alp-cream/80 mt-4"><input type="checkbox" checked={editingLibItem.active} onChange={(e) => lif('active', e.target.checked)} data-testid="lib-item-active" /> Visible to clients</label>
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setEditingLibItem(null)} className="px-5 py-2.5 text-xs tracking-[0.15em] uppercase text-alp-gray border border-alp-line rounded hover:text-alp-cream transition-colors">Cancel</button>
            <button onClick={saveLibItem} data-testid="save-lib-item-btn" className="px-6 py-2.5 text-xs tracking-[0.15em] uppercase bg-alp-gold text-alp-black font-semibold rounded hover:bg-alp-goldlight transition-colors">Save Guide</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* P&L ORDERS DRILL-DOWN */}
      <Dialog open={!!pnlOrdersFor} onOpenChange={(o) => !o && setPnlOrdersFor(null)}>
        <DialogContent className="bg-alp-card border-alp-line text-alp-cream max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-alp-cream">Orders for {pnlOrdersFor?.name}</DialogTitle>
          </DialogHeader>
          {pnlOrders.length === 0 ? (
            <p className="text-alp-gray py-8 text-center">No orders found for this product.</p>
          ) : (
            <div className="space-y-2" data-testid="pnl-orders-list">
              {pnlOrders.map((o) => {
                const line = (o.items || []).find((it) => it.id === pnlOrdersFor.id || it.name === pnlOrdersFor.name);
                return (
                  <div key={o.orderNumber} data-testid={`pnl-order-${o.orderNumber}`} className="border border-alp-line rounded p-3 flex flex-wrap items-center gap-3 text-sm">
                    <span className="text-alp-gold font-medium">{o.orderNumber}</span>
                    <span className="text-alp-cream flex-1 min-w-[120px]">{o.customer?.fullName}</span>
                    <span className="text-alp-gray">Qty: {line?.qty ?? '\u2014'}</span>
                    <span className="text-alp-gray">{(o.createdAt || '').slice(0, 10)}</span>
                    <span className="text-[10px] tracking-[0.12em] uppercase border border-alp-gold/40 text-alp-gold rounded px-2 py-0.5">{o.orderStatus}</span>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}
